import React, { useMemo, useRef, useCallback, useState, useEffect, useLayoutEffect, useDeferredValue } from 'react';
import { createPortal } from 'react-dom';
import { useAnalytics } from '../../../../core/analytics';
import {
  ColumnResizeMode,
  ColumnSizingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { AlertTriangle, RotateCcw, Pin } from 'lucide-react';
import type { QueryColumn, QueryResultSet, ResultCacheCapabilities } from '../../../../types/queryExecution';
import type { ResultDisplayMode, QueryMessage } from '../../core/types/workspaceTypes';
import { StackedResultsView } from './StackedResultsView';
import { StackedOutputSection } from './StackedOutputSection';
import { OutputPanel } from './OutputPanel';
import { hasUserPrintMessages } from '../utils/plaintextResultsUtils';
import { formatRowCount } from '../utils/formatRowCount';
import { hasWideValueColumns as computeHasWideValueColumns } from '../utils/csvExportTelemetry';
import { PlaintextResultsView } from './PlaintextResultsView';
import { RowSidePanelContent } from './RowSidePanelContent';
import { ResultsGridActions } from './ResultsGridActions';
import { guessJsonColumn, isJsonClassifiedColumn, isStringDataType } from '../utils/jsonDetection';
import { categorizeJsonColumn, type JsonColumnInput } from '../utils/jsonColumnCategorization';
import { buildDismissedEnumKeySet, categorizeEnumColumn, fkColumnSuggestsEnum, isEnumDeclarableType, nameSuggestsEnum, type EnumColumnInput } from '../utils/enumColumnCategorization';
import { resolveLookupEnumTarget } from '../utils/lookupEnumTarget';
import {
  isOnDemandLookupColumn,
  resolveEffectiveLookupDeclaration,
} from '../utils/effectiveLookupDeclaration';
import type { EnumLookupTarget } from '../../../../types/electron';
import {
  categorizeSpatialColumn,
  sniffSpatialBlobColumns,
  findSpatialPairPartner,
  suggestSpatialPairs,
  sampleDeclarableTextColumns,
  type SpatialColumnInput,
} from '../utils/spatialColumnCategorization';
import { DATABASE_ENGINE_IDS, type DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { engineSupports } from '../../../../core/engines/engineCapabilities';
import type { SpatialGlyphState } from '../components/grid-glyphs/SpatialHeaderGlyph';
import { detectContentMode, type ExpandedEditMode } from '../../table-explorer/utils/expandedEditDetection';
import { useCellRenderer, type EditModeSnapshot } from '../hooks/useCellRenderer';
import { useMetaInfo } from '../hooks/useMetaInfo';
import { useLookupLabelMaps } from '../hooks/useLookupLabelMaps';
import { useLookupLabelSelfHeal } from '../hooks/useLookupLabelSelfHeal';
import { useOnDemandLookupLabels } from '../hooks/useOnDemandLookupLabels';
import { openDeclareJsonColumnDialog } from './filters/declareJsonColumnDialogStore';
import { openDeclareEnumColumnDialog } from './filters/declareEnumColumnDialogStore';
import {
  openDeclareSpatialColumnDialog,
  type DeclareSpatialColumnDialogTrigger,
} from './filters/declareSpatialColumnDialogStore';
import { openEnumDetailsDialog } from './meta-info/enumDetailsDialogStore';
import { openJsonInspectorDialog } from './meta-info/jsonInspectorDialogStore';
import { openLooseRelationshipEditorDialog } from './filters/looseRelationshipEditorDialogStore';
import { isLooseFkCandidateType } from '../utils/inferLooseRelationship';
import { useExportCopy } from '../hooks/useExportCopy';
import { registerGridExport } from '../store/gridExportRegistry';
import { GridHeaderRow } from './GridHeaderRow';
import { openDataProfileTab } from '../../data-profile/services/dataProfileNavigation';
import { openFindUsagesTab } from '../../dependency-viewer/services/findUsagesNavigation';
import { isFindUsagesSupportedEngine } from '../../dependency-viewer/services/findUsagesEngine';
import { GridStatusInline } from './GridStatusInline';
import { ExpandedValueDialog } from '../../table-explorer/components/ExpandedValueDialog';
import { toast } from '../../../../components/ui/toaster';
import type { ResultColumnTarget } from '../utils/resultColumnWhereField';
import type { AddColumnToFilter } from '../utils/addColumnToFilter';
import { writeClipboardText } from '../../../../lib/clipboard';
import { RandomDotmSpinner } from '../../../../components/ui/RandomDotmSpinner';
import { useProFeature } from '../../../../core/licensing/hooks/useProFeature';
import { PersonalModeExportLimitMessage } from './PersonalModeExportLimitMessage';
import { UpgradeDialog } from '../../../../core/licensing/components/UpgradeDialog';
import { useQueryEditorMcpStore } from '../../query-editor/store/queryEditorMcpStore';
import { ResultsEditToolbar } from '../../query-editor/components/ResultsEditToolbar';
import { resultSetFkKey, useResultSetRealFkMap, useResultSetNativeEnumColumnIds } from './cell-edit/useResultSetRealFkMap';
import { CellContextMenu } from './CellContextMenu';
import { ResultsGridDataRow, type ResultsGridRowCtx } from './ResultsGridDataRow';
import { useCellSelection, buildCopyText, buildCopyTextAsync, buildCopyRowText, buildCopyHtml, hasOutOfBufferCells, parseCellKey } from '../hooks/useCellSelection';
import { useSpatialCopyTransform } from '../hooks/useSpatialCopyTransform';
import { writeRichToClipboard, columnValuesToCsvList } from '../utils/clipboardFormatters';
import { useCellAggregates } from '../hooks/useCellAggregates';
import { CellAggregateBar } from './CellAggregateBar';
import { useColumnPinning } from '../hooks/useColumnPinning';
import { computeColumnProfiles, type ColumnProfileStats } from '../utils/columnProfiling';
import { compareValues, isNumericDataType } from '../utils/sortComparator';
import { isNumericColumn, shouldGroupNumericColumn } from '../utils/columnAlignment';
import { useAppStore } from '../../../../store/appStore';
import { applyResultFilters } from '../utils/resultFiltering';
import { useWindowedData } from '../hooks/useWindowedData';
import { useWindowedGrid } from '../hooks/useWindowedGrid';
import { useWindowedPlaintextRows } from '../hooks/useWindowedPlaintextRows';
import { SkeletonRows } from './SkeletonRow';
import { getVirtualPadding, needsProportionalScroll, rowIndexToScrollTop, scrollTopToRowIndex } from '../hooks/useProportionalScroll';
import { trimGapSkeletonWindow } from '../hooks/gapSkeletonWindow';
import { getResultCacheCellValue } from '../services/resultCacheApi';
import { fetchFullColumnValues } from '../services/resultCacheFullValues';
import { resolveSpatialCellInput, type ExpandedSpatialPayload, type SpatialAccessorContext, type SpatialColumnDeclaration } from '../utils/spatialValueAccessor';
import { SpatialCompareDialog } from '../../spatial/compare/SpatialCompareDialog';
import { useRowSidePanelResize, ROW_SIDE_PANEL_MIN_WIDTH, ROW_SIDE_PANEL_MAX_WIDTH } from '../hooks/useRowSidePanelResize';

type ViewMode = 'tabs' | 'stack';

// Stack view limits (no virtualization, so we limit rows for performance and UX)
const STACK_VIEW_MAX_ROWS_PER_RESULTSET = 100;
const STACK_VIEW_MAX_TOTAL_ROWS = 500;

// Stable fallback for result-set tabs that have no saved filter state.
// A fresh `{}` per render would cause referential-inequality loops in useWindowedGrid's filter sync.
const EMPTY_FILTER_STATE: { columnFilters: Record<string, string>; globalFilter: string } = { columnFilters: {}, globalFilter: '' };

const ROW_SIDE_PANEL_DEFAULT_WIDTH = 320;

export type ResultsGridEditMode = {
  hasEditableColumns: boolean;
  /** Why editing is unavailable (only set when hasEditableColumns is false) */
  readOnlyReason?: string;
  isEditMode: boolean;
  pendingChangeCount: number;
  isSaving: boolean;
  isColumnEditable: (columnName: string) => boolean;
  getCellPendingValue: (rowPk: Record<string, unknown>, field: string) => unknown | undefined;
  getPrimaryKeyForCell: (row: Record<string, unknown>, columnName: string) => Record<string, unknown> | null;
  onToggleEditMode: () => void;
  onSave: () => void;
  onDiscard: () => void;
  onCellEdit: (row: Record<string, unknown>, columnName: string, oldValue: unknown, newValue: unknown) => void;
  onPreview?: () => void;
};

interface ResultsGridProps {
  /** @deprecated Use resultSets instead */
  results?: unknown[];
  /** @deprecated Use resultSets instead */
  columns?: { id?: string; name: string }[];
  /** Engine-neutral result sets (supports multiple) */
  resultSets?: QueryResultSet[];
  /** Messages from query execution (shown in plaintext mode) */
  messages?: QueryMessage[];
  isLoading?: boolean;
  className?: string;
  /** Display mode: 'grid' (default) or 'plaintext' */
  displayMode?: ResultDisplayMode;
  /** Optional tab ID for MCP command consumption */
  tabId?: string;
  /** Optional edit mode controls for query editor results */
  editMode?: ResultsGridEditMode;
  /** Zoom level for the results table (1 = 100%) */
  zoomLevel?: number;
  /** Connection ID for FK navigation (lazy constraint resolution) */
  connectionId?: string;
  /** Database name for FK navigation */
  database?: string;
  /** Engine ID for engine-aware formatting (INSERT quoting, etc.) */
  engineId?: string;
  /** Cache URI for windowed result fetching (when present, rows are fetched on demand from Electron) */
  cacheUri?: string;
  /** Per-result-set cache URIs (for windowed plaintext rendering of every result set) */
  resultSetCacheUris?: readonly (string | undefined)[];
  /** Total row count in the cached result set (required when cacheUri is set) */
  remoteTotalRowCount?: number;
  /** Monotonically increasing counter — bumped on each query execution so windowed buffer resets */
  cacheGeneration?: number;
  /** Capability flags from the result source (canSort, canFilter, etc.) */
  capabilities?: ResultCacheCapabilities;
  /** Callback when user clicks "Add to WHERE" in cell context menu — with the column's type, provenance and position */
  onAddToWhere?: (columnName: string, value: unknown, column: ResultColumnTarget) => void;
  /** Column header "Add to Filter" — the Query tab's Visual Query Editor condition editor for the column. */
  addColumnToFilter?: AddColumnToFilter;
  /** Callback to pin the current results (multi-pin: always creates a new pinned tab) */
  onPin?: () => void;
  /** S20 — pin to dashboard; the grid passes the active result set's shape
   * (columns + a row sample) so the sheet's autopilot can suggest a tile type. */
  onPinToDashboard?: (shape: { columns: QueryColumn[]; rows: Record<string, unknown>[] } | undefined) => void;
  /** Callback when the active result set tab changes (for parent sync) */
  onActiveResultSetChange?: (index: number) => void;
  /** Callback with the first visible row index (0-based) on scroll, for status bar position display */
  onScrollPositionChange?: (firstVisibleRow: number) => void;
  /** Callback when the result-source generation changes (sort/filter mutations) */
  onSourceGenerationChange?: (generation: number) => void;
  /** Callback to re-execute the query (e.g., after a source failure) */
  onReExecute?: () => void;
  /** Whether the result source is still populating (disables sort/filter UI) */
  isPopulating?: boolean;
  /** Callback to cancel the in-progress query (shown as a Stop button next to the populating indicator) */
  onCancelPopulation?: () => void;
  /** Whether a cancel request is already in flight */
  isCancelling?: boolean;
  /**
   * Whether the in-flight query has crossed the long-running threshold (8 s).
   * Switches the "Executing query…" affordance to its amber "taking longer" form.
   */
  isLongRunning?: boolean;
  /** Surface label propagated to FK-cell analytics. Defaults to 'query_editor' if undefined. */
  analyticsSurface?: 'query_editor' | 'table_explorer';
  /**
   * Whether the toolbar states the settled row count for a single result set.
   * Defaults to `true` — the standalone Query Editor grid owns its count.
   *
   * Set to `false` when the grid is *embedded* in a surface that already states
   * the same fact next to it (FK Preview's "Showing N rows" line, a notebook
   * cell's status line) so the count is stated once, not twice. Only the settled
   * count is suppressed: in-flight populating/awaiting progress and the
   * per-result-set counts on multi-result-set tabs are different facts and are
   * always shown.
   */
  showRowCountSummary?: boolean;
  /**
   * PP-16 — whether the grid renders its own toolbar row (result-set tabs,
   * edit-mode controls, filter/profiling/copy/export/pin actions). Defaults
   * to `true` — the standalone Query Editor grid owns this chrome.
   *
   * Set to `false` when the grid is embedded in a surface that already
   * renders its own single output bar (a notebook cell's toolbar) and a
   * single result set, so the cell shows exactly one bar of output chrome
   * instead of two stacked ones. Only meaningful for a single result set —
   * a caller with multiple result sets should keep this `true` so the
   * Result-1/Result-2 tabs (irreplaceable navigation, not decorative
   * chrome) stay reachable.
   */
  showToolbar?: boolean;
  /**
   * Fix round 1 (C1) — when `showToolbar` is `false`, the grid's own
   * actions cluster (column profiling / result filter / pin / copy &
   * export / row-side-panel toggle) has nowhere to render. Dropping it
   * silently deletes real functionality — there is no other reachable
   * path to CSV/XLSX export, filtering, profiling, or pin. Pass a DOM
   * node here (a container the embedding surface renders inside its OWN
   * single output bar, e.g. via a callback ref) and the actions cluster
   * portals into that node instead of disappearing. Ignored when
   * `showToolbar` is `true` (the grid's own bar already hosts the
   * actions cluster in place) or when `hasMultipleResultSets` is true
   * (that case keeps `showToolbar` on for its tabs, per the doc above).
   */
  actionsPortalContainer?: HTMLElement | null;
  /**
   * Controlled row selection (T13 spatial Map view). When provided (≠ undefined) the grid's selected
   * row is DRIVEN by this prop: on change it selects the row, scrolls it into view (even past the
   * initial window buffer — A21/G5), and highlights it. `onSelectedRowIndexChange` echoes user row
   * clicks back out so the map can highlight the matching feature. Uncontrolled when omitted.
   */
  externalSelectedRowIndex?: number | null;
  onSelectedRowIndexChange?: (index: number | null) => void;
  /**
   * CSV "Export All" finished but skipped unreadable rows (S2) — forwarded from
   * `useWindowedGrid` with the 0-based absolute indexes so the owning surface
   * (QueryEditorTab) can raise its "Skipped rows" bottom tab.
   */
  onExportSkippedRows?: (skippedRows: number[]) => void;
  /** "Review skipped rows" toast action — activates the owning surface's Skipped-rows tab. */
  onReviewSkippedRows?: () => void;
}

// 8 rows of overscan (was 15): with memoized rows the window refills one row
// per boundary crossing during ordinary scrolling, and the fast-scroll gap
// branch covers flicks — the remaining role of overscan is hiding the refill
// of a few rows per wheel notch, which 8 covers with margin. On 74-column
// tables the cut saves ~1,000 off-screen cells per full-window mount
// (measured ~25-30% off page-jump mounts).
const DEFAULT_OVERSCAN = 8;
const MIN_COLUMN_WIDTH = 50;
const EMPTY_ROWS: Record<string, unknown>[] = [];
const MAX_AUTO_COLUMN_WIDTH = 400; // Max width in auto mode to prevent unbounded growth
const ROW_HEIGHT = 32; // estimated row height in px

const EMPTY_RESULTS: unknown[] = [];
const EMPTY_COLUMNS: { id?: string; name: string }[] = [];
const EMPTY_COLUMN_SIZING: ColumnSizingState = {};

export const ResultsGrid = React.memo(function ResultsGrid({
  results = EMPTY_RESULTS,
  columns = EMPTY_COLUMNS,
  resultSets,
  messages,
  isLoading = false,
  className = '',
  displayMode = 'grid',
  tabId,
  editMode,
  zoomLevel = 1,
  connectionId,
  database,
  engineId,
  cacheUri,
  resultSetCacheUris,
  remoteTotalRowCount,
  cacheGeneration,
  capabilities,
  onAddToWhere,
  addColumnToFilter,
  onPin,
  onPinToDashboard,
  onActiveResultSetChange,
  onScrollPositionChange,
  onSourceGenerationChange,
  onReExecute,
  isPopulating = false,
  onCancelPopulation,
  isCancelling = false,
  isLongRunning = false,
  analyticsSurface,
  showRowCountSummary = true,
  showToolbar = true,
  actionsPortalContainer = null,
  externalSelectedRowIndex,
  onSelectedRowIndexChange,
  onExportSkippedRows,
  onReviewSkippedRows,
}: ResultsGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  // scrollRafRef removed — scroll updates are now synchronous for instant skeleton rendering
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [isFixedLayout, setIsFixedLayout] = useState(false);
  const [activeResultSetIndex, setActiveResultSetIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('tabs');
  const [isOutputActive, setIsOutputActive] = useState(false);
  const [expandedTextDialog, setExpandedTextDialog] = useState<{
    title: string; value: string; mode: ExpandedEditMode;
    columnId?: string; rowData?: Record<string, unknown>;
    isLoadingFullValue?: boolean;
    /** T11 — present when opened from a spatial cell; drives the map pane. */
    spatial?: ExpandedSpatialPayload;
  } | null>(null);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isCopyMenuOpen, setIsCopyMenuOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [isRowSidePanelOpen, setIsRowSidePanelOpen] = useState(false);
  const [rowSidePanelWidthPx, setRowSidePanelWidthPx] = useState(ROW_SIDE_PANEL_DEFAULT_WIDTH);
  const resultsSplitRef = useRef<HTMLDivElement>(null);
  const [showColumnProfiling, setShowColumnProfiling] = useState(false);
  const [sortStateByTab, setSortStateByTab] = useState<Record<number, { columnId: string; direction: 'asc' | 'desc' } | null>>({});
  const sortState = sortStateByTab[activeResultSetIndex] ?? null;
  const setSortState = useCallback((newState: { columnId: string; direction: 'asc' | 'desc' } | null) => {
    setSortStateByTab(prev => ({ ...prev, [activeResultSetIndex]: newState }));
  }, [activeResultSetIndex]);
  const [showResultFilter, setShowResultFilter] = useState(false);
  // Filter state is scoped per result-set tab — remembered when switching tabs
  const [filtersByTab, setFiltersByTab] = useState<Record<number, { columnFilters: Record<string, string>; globalFilter: string }>>({});
  const currentTabFilters = filtersByTab[activeResultSetIndex] ?? EMPTY_FILTER_STATE;
  const columnFilters = currentTabFilters.columnFilters;
  const globalFilter = currentTabFilters.globalFilter;
  const setColumnFilters = useCallback((update: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => {
    setFiltersByTab(prev => {
      const current = prev[activeResultSetIndex] ?? { columnFilters: {}, globalFilter: '' };
      const newFilters = typeof update === 'function' ? update(current.columnFilters) : update;
      return { ...prev, [activeResultSetIndex]: { ...current, columnFilters: newFilters } };
    });
  }, [activeResultSetIndex]);
  const setGlobalFilter = useCallback((value: string) => {
    setFiltersByTab(prev => {
      const current = prev[activeResultSetIndex] ?? { columnFilters: {}, globalFilter: '' };
      return { ...prev, [activeResultSetIndex]: { ...current, globalFilter: value } };
    });
  }, [activeResultSetIndex]);
  const filterInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const selectedRowRef = useRef<Record<string, unknown> | null>(null);

  // Inline cell editing state
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnId: string } | null>(null);
  // Cell the ONE delegated context menu is open on (see the CellContextMenu
  // around <tbody> below) — replaces the per-cell Radix menu that used to
  // dominate scroll-render cost on wide results.
  const [menuCell, setMenuCell] = useState<{ rowIndex: number; columnId: string } | null>(null);
  // Bumped on grid-initiated edit commits so memoized rows repaint pending-cell
  // washes even when `pendingChangeCount` alone wouldn't change (e.g. editing
  // the same cell twice in a row).
  const [gridEditVersion, setGridEditVersion] = useState(0);
  const [sidePanelEditingColumn, setSidePanelEditingColumn] = useState<string | null>(null);

  // Ref so columnDefs cell renderer can read current edit mode without being a dependency
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;

  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const { canUse: canExportFull } = useProFeature('export-full');

  // Windowed data mode: fetch rows on demand from Electron cache
  const isWindowed = Boolean(cacheUri && remoteTotalRowCount !== undefined);

  const canSort = isPopulating ? false : (isWindowed ? (capabilities ? capabilities.canSort : false) : true);
  const canFilter = isPopulating ? false : (isWindowed ? (capabilities ? capabilities.canFilter : false) : true);

  useEffect(() => {
    if (isWindowed && !capabilities) {
      console.warn('[ResultsGrid] windowed mode without capabilities prop — defaulting to disabled.');
    }
  }, [isWindowed, capabilities]);

  // Calculate stack view availability based on row limits
  const stackViewStatus = useMemo(() => {
    if (!resultSets || resultSets.length <= 1) {
      return { available: false, reason: 'Single result set - use default view' };
    }

    let totalRows = 0;
    for (let i = 0; i < resultSets.length; i++) {
      const rs = resultSets[i];
      const rows = Array.isArray(rs.rows) ? rs.rows : [];
      const rowCount = rs.rowCount ?? rows.length;

      if (rowCount > STACK_VIEW_MAX_ROWS_PER_RESULTSET) {
        return {
          available: false,
          reason: `Result ${i + 1} has ${rowCount.toLocaleString()} rows (max ${STACK_VIEW_MAX_ROWS_PER_RESULTSET})`,
        };
      }
      totalRows += rowCount;
    }

    if (totalRows > STACK_VIEW_MAX_TOTAL_ROWS) {
      return {
        available: false,
        reason: `Total ${totalRows.toLocaleString()} rows exceeds limit (max ${STACK_VIEW_MAX_TOTAL_ROWS})`,
      };
    }

    return { available: true, reason: null };
  }, [resultSets]);

  // Determine if we have multiple result sets
  const hasMultipleResultSets = resultSets && resultSets.length > 1;

  // Get the active result set (or use legacy results/columns)
  const activeResultSet = useMemo(() => {
    if (resultSets && resultSets.length > 0) {
      const idx = Math.min(activeResultSetIndex, resultSets.length - 1);
      return resultSets[idx];
    }
    return null;
  }, [resultSets, activeResultSetIndex]);

  // Reset active result set, view mode, and filters on new execution.
  // Windowed mode: keyed off cacheGeneration (stable during population progress ticks).
  // Non-windowed: keyed off resultSets identity (changes once on execution complete).
  useEffect(() => {
    if (cacheGeneration !== undefined) return;
    setActiveResultSetIndex(0);
    setViewMode('tabs');
    setIsOutputActive(false);
    setFiltersByTab({});
    setShowResultFilter(false);
  }, [resultSets, cacheGeneration]);  
  useEffect(() => {
    if (cacheGeneration === undefined) return;
    setActiveResultSetIndex(0);
    setViewMode('tabs');
    setIsOutputActive(false);
    setFiltersByTab({});
    setShowResultFilter(false);
  }, [cacheGeneration]);

  // Notify parent when active result set changes
  useEffect(() => {
    onActiveResultSetChange?.(activeResultSetIndex);
  }, [activeResultSetIndex, onActiveResultSetChange]);

  // Normalize columns to ensure each has an id
  // Use resultSets if available, otherwise fall back to legacy columns
  // Pull declared loose-FK relationships for the current connection/database so
  // result-grid columns whose source matches a MetaInfo entry render as
  // clickable FK cells (with the loose teal/dashed styling).
  const { data: metaInfo } = useMetaInfo(connectionId ?? null, database ?? null);
  const looseFkLookup = useMemo(() => {
    const map = new Map<string, true>();
    for (const rel of metaInfo?.looseRelationships ?? []) {
      map.set(`${rel.fromSchema}|${rel.fromTable}|${rel.fromColumn}`, true);
    }
    return map;
  }, [metaInfo]);

  // IMPORTANT: `key` is what the ROW OBJECTS are keyed by — the producer's
  // `id` when it set one, else the column name, even when that name is `''`
  // (a driver row for an unnamed column IS keyed `''`; substituting anything
  // else here reads `undefined` and renders NULL). `id` is for React/TanStack
  // table, which cannot take an empty string, so it falls back to a positional
  // one; `name` is the header label, with a `Column N` fallback for a blank name.
  const normalizedColumns = useMemo(() => {
    if (activeResultSet) {
      const cols = activeResultSet.columns ?? [];
      const sourceTables = new Set<string>();
      for (const col of cols) {
        if (!col.sourceTable) continue;
        sourceTables.add(col.sourceTable);
      }

      const showSourceTables = sourceTables.size > 1;

      return cols.map((col, index) => {
        const isLoose =
          !col.isForeignKey &&
          !!col.sourceSchema &&
          !!col.sourceTable &&
          !!col.sourceColumn &&
          looseFkLookup.has(`${col.sourceSchema}|${col.sourceTable}|${col.sourceColumn}`);
        return {
          id: col.id || col.name || `col_${index}`,
          key: col.id ?? col.name, // Key for row data access
          name: col.name || `Column ${index + 1}`,
          dataType: col.dataType,
          isLong: col.isLong,
          isPrimaryKey: col.isPrimaryKey,
          isForeignKey: col.isForeignKey || isLoose,
          isLoose,
          nullable: col.nullable,
          sourceSchema: col.sourceSchema,
          sourceTable: col.sourceTable,
          sourceColumn: col.sourceColumn,
          showSourceTable: showSourceTables && Boolean(col.sourceTable),
          jsonClassification: col.jsonClassification,
        };
      });
    }
    return columns.map((col, index) => ({
      id: col.id || col.name || `col_${index}`,
      key: col.id ?? col.name,
      name: col.name || col.id || `Column ${index + 1}`,
      dataType: undefined,
      isLong: undefined,
      isPrimaryKey: undefined,
      isForeignKey: undefined,
      isLoose: undefined,
      nullable: undefined,
      sourceSchema: undefined,
      sourceTable: undefined,
      sourceColumn: undefined,
      showSourceTable: false,
      jsonClassification: undefined,
    }));
  }, [activeResultSet, columns, looseFkLookup]);

  // PP-01 — numeric alignment sets. `numericColumnIds` drives right alignment
  // (header label, inline filter input, cells and the NULL marker);
  // `groupedNumericColumnIds` is the quantity-only subset that also gets
  // thousands grouping in the user's `numericGrouping` mode (empty when the
  // mode is `off`, so no cell ever calls the formatter). Display only — every
  // copy/export path reads the raw row value, never the rendered text.
  // Mirrored in `TableExplorerGrid`. The mode is read ONCE here and handed
  // down through the row ctx — never subscribed per cell.
  const numericGrouping = useAppStore((state) => state.numericGrouping);
  const { numericColumnIds, groupedNumericColumnIds } = useMemo(() => {
    const numeric = new Set<string>();
    const grouped = new Set<string>();
    for (const column of normalizedColumns) {
      if (!isNumericColumn(column)) continue;
      numeric.add(column.id);
      if (numericGrouping !== 'off' && shouldGroupNumericColumn(column)) grouped.add(column.id);
    }
    return { numericColumnIds: numeric, groupedNumericColumnIds: grouped };
  }, [normalizedColumns, numericGrouping]);

  // O(1) column lookup for cell rendering / the delegated context menu —
  // replaces per-cell `normalizedColumns.find(...)` scans.
  const columnById = useMemo(
    () => new Map(normalizedColumns.map((c) => [c.id, c])),
    [normalizedColumns]
  );
  // A column as "Add to WHERE" / "Add to Filter" see it: its type, provenance and position.
  const resultColumnTarget = useCallback((columnId: string): ResultColumnTarget | null => {
    const col = columnById.get(columnId);
    if (!col) return null;
    return {
      name: col.name,
      index: normalizedColumns.findIndex((c) => c.id === columnId),
      count: normalizedColumns.length,
      dataType: col.dataType,
      isForeignKey: col.isForeignKey,
      sourceSchema: col.sourceSchema,
      sourceTable: col.sourceTable,
      sourceColumn: col.sourceColumn,
    };
  }, [columnById, normalizedColumns]);

  // Real-FK target lookup across every unique source table in the result.
  // Drives the FK cell editor for QE — TE already has this via foreignKeyMap
  // built from the table's own metadata.
  const realFkMap = useResultSetRealFkMap({
    connectionId: connectionId ?? null,
    database: database ?? null,
    columns: normalizedColumns,
    looseRelationships: metaInfo?.looseRelationships,
  });

  // Native-enum flag per result column, resolved from the same source-table
  // metadata cache. Table Explorer gets this for free (`useTableMetadata` →
  // `column.isNativeEnumType`); QE result columns carry only a type string,
  // and Postgres resolves that through `format_type` — the enum's own type
  // name, never `USER-DEFINED` — so without this a PG native enum would show
  // no header glyph and no enum cell editor here.
  const nativeEnumSourceColumnIds = useResultSetNativeEnumColumnIds({
    connectionId: connectionId ?? null,
    database: database ?? null,
    columns: normalizedColumns,
  });

  // Transform rows if they are arrays into objects keyed by column id
  // Use resultSets if available, otherwise fall back to legacy results
  const normalizedResults = useMemo<Record<string, unknown>[]>(() => {
    if (activeResultSet) {
      // ResultSet rows are already in object format
      return Array.isArray(activeResultSet.rows) ? activeResultSet.rows : [];
    }

    if (!results.length) return [];

    const firstRow = results[0];
    if (!Array.isArray(firstRow)) {
      return results as Record<string, unknown>[];
    }

    return results.map((row) => {
      if (!Array.isArray(row)) return row as Record<string, unknown>;

      const obj: Record<string, unknown> = {};
      normalizedColumns.forEach((col, index) => {
        obj[col.id] = row[index];
      });
      return obj;
    });
  }, [activeResultSet, results, normalizedColumns]);

  // Apply client-side filters (before sorting) — skipped in windowed mode (filter runs in Electron)
  const columnKeys = useMemo(() => normalizedColumns.map((c) => c.key), [normalizedColumns]);

  // Engine id validated against the known engine registry. Computed early
  // (moved up from its former position further down this file, T5) because
  // `nativeSpatialColumnIds` below feeds `useWindowedGrid`'s windowed "Export
  // All" WKT decode, and hook calls need their inputs declared beforehand.
  const validEngine: DatabaseEngineId | null = useMemo(() => {
    if (!engineId) return null;
    return (DATABASE_ENGINE_IDS as readonly string[]).includes(engineId)
      ? (engineId as DatabaseEngineId)
      : null;
  }, [engineId]);

  // Find usages needs a SQL module catalog; kusto/ADX has none (see
  // `isFindUsagesSupportedEngine`). Gate the header-menu entry, not just the SQL.
  const findUsagesSupported = useMemo(() => isFindUsagesSupportedEngine(engineId), [engineId]);

  // Native spatial columns → gray ⌖ glyph + sniffer-derived cell summary.
  // Gated by the spatial capability so kusto (and any non-spatial engine) opts
  // out entirely. SQLite unions in a BLOB/affinity-less value sniff over the
  // loaded window (zero I/O) since GeoPackage/SpatiaLite columns are declared
  // BLOB and miss name-based categorization. metaInfo is threaded for T18.
  // Computed early (moved up, T5) — see `validEngine` note above.
  const nativeSpatialColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    if (!engineSupports(validEngine, 'spatial', 'supportsSpatialTypes')) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: SpatialColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
      };
      if (categorizeSpatialColumn(input, metaInfo) === 'native-spatial') {
        ids.add(column.id);
      }
    }
    if (validEngine === 'sqlite') {
      const blobInputs: SpatialColumnInput[] = normalizedColumns.map((column) => ({
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable ?? '',
        name: column.key,
        dataType: column.dataType ?? '',
      }));
      const promoted = sniffSpatialBlobColumns(blobInputs, normalizedResults.slice(0, 20));
      for (const column of normalizedColumns) {
        if (promoted.has(column.key)) ids.add(column.id);
      }
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns, normalizedResults]);

  // T18 — user-declared coordinate columns. `declaredSpatialColumns` maps the
  // grid columnId → the declaration (for a pair, only the LAT column is keyed;
  // its `lngColumn` names the sibling). `declarableSpatialColumnIds` gates the
  // "Declare as coordinates…" header entry on non-native, non-declared columns.
  // Deliberately NOT gated by the spatial capability — declared columns render
  // on the map on ANY engine, even ones with no native spatial type.
  const declaredSpatialColumns = useMemo(() => {
    const map = new Map<string, SpatialColumnDeclaration>();
    if (!metaInfo) return map;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn) continue;
      if (nativeSpatialColumnIds.has(column.id)) continue;
      const decl = metaInfo.spatialColumns.find(
        (d) =>
          d.schema === (column.sourceSchema ?? '') &&
          d.table === column.sourceTable &&
          d.column === column.sourceColumn,
      );
      if (decl) map.set(column.id, decl);
    }
    return map;
  }, [metaInfo, normalizedColumns, nativeSpatialColumnIds]);

  const declaredSpatialColumnIds = useMemo(
    () => new Set(declaredSpatialColumns.keys()),
    [declaredSpatialColumns],
  );

  // T19 — declarable suggestion heuristic (two arms, spec §3.4): a name-only
  // WKT/GeoJSON-text arm (`categorizeSpatialColumn`'s own `spatial-declarable`
  // category), a lat/lng pair NAME arm (`suggestSpatialPairs`, zero I/O), and a
  // TEXT-column VALUE-SAMPLING arm over the already-loaded window
  // (`sampleDeclarableTextColumns`, zero EXTRA I/O — never queries the DB).
  // Replaces the pre-T19 "every non-native, non-declared column" placeholder.
  const declarableSpatialColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    const candidates: SpatialColumnInput[] = [];
    const idByName = new Map<string, string>();
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      if (nativeSpatialColumnIds.has(column.id)) continue;
      if (declaredSpatialColumnIds.has(column.id)) continue;
      const input: SpatialColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
      };
      candidates.push(input);
      idByName.set(column.sourceColumn, column.id);
      if (categorizeSpatialColumn(input, metaInfo) === 'spatial-declarable') {
        ids.add(column.id);
      }
    }
    for (const name of suggestSpatialPairs(candidates)) {
      const id = idByName.get(name);
      if (id) ids.add(id);
    }
    for (const name of sampleDeclarableTextColumns(candidates, normalizedResults.slice(0, 20))) {
      const id = idByName.get(name);
      if (id) ids.add(id);
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns, nativeSpatialColumnIds, declaredSpatialColumnIds, normalizedResults]);

  // T5 — WKT copy/export parity: single shared transform, injected at every
  // copy seam below (Ctrl+C, context menu, header "Copy values", row side
  // panel) plus threaded into useExportCopy/useWindowedGrid above. A stable
  // callback (ref-backed) so it can be declared once here regardless of where
  // each consuming effect/handler sits in this file.
  const spatialCopyTransform = useSpatialCopyTransform(
    nativeSpatialColumnIds,
    validEngine,
    (columnId) => normalizedColumns.find((c) => c.id === columnId)?.dataType,
    declaredSpatialColumns,
  );

  // Export-failure telemetry: whether any column type can hold huge values
  // (see csvExportTelemetry.ts).
  const hasWideValueColumns = useMemo(
    () => computeHasWideValueColumns(normalizedColumns),
    [normalizedColumns],
  );

  // "Show row" on the CSV-export skipped-rows toast jumps the grid to the row
  // the export could not read. The jump needs virtualization state (row count,
  // viewport height, proportional mode) that is computed far below this call
  // site, so `useWindowedGrid` gets an identity-stable trampoline that reads
  // the real implementation (`jumpToRow`, further down) through a ref.
  const jumpToRowRef = useRef<((rowIndex: number) => void) | null>(null);
  const scrollToRow = useCallback((rowIndex: number) => {
    jumpToRowRef.current?.(rowIndex);
  }, []);

  // Windowed grid: remote sort/filter dispatch, CSV export, and total row count tracking
  const {
    windowedTotalRowCount, effectiveTotalRowCount, dataVersion,
    isRemoteSorting, handleWindowedSortChange, handleExportAllCsv,
    isExporting: isWindowedExporting, sourceGeneration,
  } = useWindowedGrid({
    isWindowed, cacheUri, remoteTotalRowCount, cacheGeneration,
    columnKeys, showResultFilter, columnFilters, globalFilter,
    capabilities,
    // T5 — WKT export parity: windowed "Export All" decodes native-spatial
    // columns to WKT via the main process before writing the CSV.
    nativeSpatialColumnIds,
    engine: validEngine ?? undefined,
    hasWideValueColumns,
    scrollToRow,
    onExportSkippedRows,
    onReviewSkippedRows,
  });
  const windowedData = useWindowedData({
    cacheUri: isWindowed ? cacheUri : undefined,
    totalRowCount: effectiveTotalRowCount,
    dataVersion,
    cacheGeneration,
    sourceGeneration,
  });
  const { bufferStart: wdBufferStart, bufferEnd: wdBufferEnd, bufferRows: wdBufferRows, sourceError: wdSourceError } = windowedData;

  // Notify parent of source generation changes (sort/filter mutations)
  useEffect(() => {
    onSourceGenerationChange?.(sourceGeneration);
  }, [sourceGeneration, onSourceGenerationChange]);

  const filteredResults = useMemo<Record<string, unknown>[]>(() => {
    if (isWindowed) return normalizedResults; // Filtering handled by Electron worker
    if (!showResultFilter) return normalizedResults;
    return applyResultFilters(normalizedResults, columnFilters, globalFilter, columnKeys);
  }, [isWindowed, normalizedResults, showResultFilter, columnFilters, globalFilter, columnKeys]);

  const isFilterActive = showResultFilter && (
    Object.values(columnFilters).some((f) => f.trim() !== '') || globalFilter.trim() !== ''
  );

  // Client-side sort — skipped in windowed mode (sort runs in Electron worker)
  const sortedResults = useMemo<Record<string, unknown>[]>(() => {
    if (isWindowed) return filteredResults; // Sorting handled by Electron worker
    if (!sortState) return filteredResults;

    const column = normalizedColumns.find((col) => col.id === sortState.columnId);
    if (!column) return filteredResults;

    const direction = sortState.direction === 'asc' ? 1 : -1;
    const numericCol = isNumericDataType(column.dataType);

    return [...filteredResults].sort((a, b) => {
      const comparison = compareValues(a[column.key], b[column.key], numericCol);
      if (comparison === 0) return 0;
      return comparison * direction;
    });
  }, [normalizedColumns, filteredResults, sortState]);

  // Column profiling stats (only computed when toggled on)
  const columnProfiles = useMemo<Map<string, ColumnProfileStats>>(() => {
    if (!showColumnProfiling || normalizedColumns.length === 0 || normalizedResults.length === 0) {
      return new Map();
    }
    const queryColumns = normalizedColumns.map((col) => ({
      name: col.name,
      id: col.id,
      dataType: col.dataType,
    }));
    const profiles = computeColumnProfiles(queryColumns, normalizedResults);
    return new Map(profiles.map((p) => [p.columnId, p]));
  }, [showColumnProfiling, normalizedColumns, normalizedResults]);

  // Cell selection + aggregate computation
  const cellSelection = useCellSelection({
    columnIds: normalizedColumns.map((c) => c.id),
    disabled: !!editMode?.isEditMode,
    rootRef: containerRef,
  });

  // ── Selection reads for memoized rows (ref-backed) ──────────────────────
  // The selection Set changes identity on EVERY mousemove of a drag-select;
  // if the row ctx carried `isCellSelected`/`handleCellMouseDown` directly,
  // each drag step would rebuild the ctx and re-render every visible row
  // (~50 x 74 cells). Rows instead get identity-STABLE trampolines that read
  // the latest selection through a ref, plus a per-row `selectionSignature`
  // prop (below) that re-renders exactly the rows whose own membership
  // changed.
  const cellSelectionRef = useRef(cellSelection);
  cellSelectionRef.current = cellSelection;
  const isCellSelectedStable = useCallback(
    (rowIndex: number, columnId: string) => cellSelectionRef.current.isCellSelected(rowIndex, columnId),
    []
  );
  const onCellMouseDownStable = useCallback(
    (rowIndex: number, columnId: string, event: React.MouseEvent) =>
      cellSelectionRef.current.handleCellMouseDown(rowIndex, columnId, event),
    []
  );
  const onCellMouseEnterStable = useCallback(
    (rowIndex: number, columnId: string) => cellSelectionRef.current.handleCellMouseEnter(rowIndex, columnId),
    []
  );
  // Per-row digest of selected columns ("" = none). Row memo comparators
  // compare this string, so a drag re-renders only rows whose membership
  // changed (typically the rows the drag boundary crossed).
  const selectionSignatureByRow = useMemo(() => {
    if (cellSelection.selectedCells.size === 0) return null;
    const byRow = new Map<number, string[]>();
    for (const key of cellSelection.selectedCells) {
      const { rowIndex, columnId } = parseCellKey(key);
      const cols = byRow.get(rowIndex);
      if (cols) cols.push(columnId);
      else byRow.set(rowIndex, [columnId]);
    }
    const signatures = new Map<number, string>();
    for (const [rowIndex, cols] of byRow) {
      // NUL separator: column ids are raw names/aliases, which may contain ','.
      signatures.set(rowIndex, cols.sort().join('\u0000'));
    }
    return signatures;
  }, [cellSelection.selectedCells]);

  const cellAggregates = useCellAggregates({
    selectedCells: cellSelection.selectedCells,
    sortedResults: isWindowed ? wdBufferRows : sortedResults,
    columns: normalizedColumns,
    rowOffset: isWindowed ? wdBufferStart : 0,
  });

  // getCellValue wrapper for windowed mode — fetches full untruncated values from backend
  const getCellValueFn = useMemo(() => {
    if (!isWindowed || !cacheUri) return undefined;
    return async (rowIndex: number, columnId: string) => {
      const response = await getResultCacheCellValue({ uri: cacheUri, rowIndex, columnId, expectedGeneration: sourceGeneration });
      if (response.error) {
        throw new Error(response.error);
      }
      return { value: response.value, generation: response.generation };
    };
  }, [isWindowed, cacheUri, sourceGeneration]);

  // T11 — windowed spatial full-value fetch. Routes through T26's BOUNDED batch
  // (single-row, single-column window) so a giant windowed geometry can't blow
  // IPC. The peek / expanded-dialog map decode the FULL value, never the preview.
  const getFullSpatialValue = useMemo(() => {
    if (!isWindowed || !cacheUri) return undefined;
    return async (rowIndex: number, columnId: string): Promise<unknown> => {
      const resp = await fetchFullColumnValues({
        uri: cacheUri,
        columnIds: [columnId],
        startIndex: rowIndex,
        count: 1,
        expectedGeneration: sourceGeneration,
      });
      return resp.values[0]?.[columnId];
    };
  }, [isWindowed, cacheUri, sourceGeneration]);

  // T30 — two-cell geometry compare eligibility: exactly two cells selected, BOTH resolving
  // through resolveSpatialCellInput (native or T18-declared). Any two rows/columns, same grid.
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const spatialCompareCandidate = useMemo(() => {
    if (cellSelection.selectedCells.size !== 2 || !validEngine) return null;
    if (nativeSpatialColumnIds.size === 0 && declaredSpatialColumnIds.size === 0) return null;
    const [keyA, keyB] = [...cellSelection.selectedCells];
    const coordA = parseCellKey(keyA);
    const coordB = parseCellKey(keyB);
    const bufferRows = isWindowed ? wdBufferRows : sortedResults;
    const bufferOffset = isWindowed ? wdBufferStart : 0;
    const rowDataA = bufferRows[coordA.rowIndex - bufferOffset];
    const rowDataB = bufferRows[coordB.rowIndex - bufferOffset];
    const ctx: SpatialAccessorContext = {
      engine: validEngine,
      nativeSpatialColumnIds,
      columnDataTypes: new Map(normalizedColumns.map((c) => [c.id, c.dataType ?? ''])),
      declaredSpatialColumns,
    };
    const inputA = resolveSpatialCellInput(coordA.columnId, rowDataA, ctx);
    const inputB = resolveSpatialCellInput(coordB.columnId, rowDataB, ctx);
    if (!inputA || !inputB) return null;
    return { a: { ...coordA, input: inputA }, b: { ...coordB, input: inputB } };
  }, [
    cellSelection.selectedCells, validEngine, nativeSpatialColumnIds, declaredSpatialColumnIds,
    declaredSpatialColumns, isWindowed, wdBufferRows, wdBufferStart, sortedResults, normalizedColumns,
  ]);

  // Ctrl+C: copy selected cell values (full fidelity in windowed mode)
  useEffect(() => {
    if (cellSelection.selectedCells.size === 0) return;

    const colKeyMap = new Map(normalizedColumns.map((c) => [c.id, c.key]));
    const colNames = new Map(normalizedColumns.map((c) => [c.id, c.name]));
    const columnIds = normalizedColumns.map((c) => c.id);
    const copyRows = isWindowed ? wdBufferRows : sortedResults;
    const copyOffset = isWindowed ? wdBufferStart : 0;

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && cellSelection.selectedCells.size > 0) {
        // Skip when the keystroke originates inside a dialog/editor/input — let it
        // handle its own copy (e.g. Monaco inside ExpandedValueDialog).
        const target = e.target as HTMLElement | null;
        if (target?.closest('[role="dialog"], .monaco-editor, input, textarea, [contenteditable="true"]')) {
          return;
        }
        e.preventDefault();
        if (isCopying) return;
        if (getCellValueFn) {
          setIsCopying(true);
          void buildCopyTextAsync(cellSelection.selectedCells, copyRows, columnIds, colKeyMap, copyOffset, getCellValueFn, sourceGeneration, colNames, spatialCopyTransform)
            .then(({ text, html, usedPreview, staleCount }) => {
              void writeRichToClipboard(text, html);
              if (staleCount > 0) {
                toast.default({ title: 'Stale values', description: `${staleCount} cell(s) used preview values due to concurrent changes.` });
              } else if (usedPreview) {
                toast.default({ title: 'Large selection', description: 'Copied preview values. Use Export for full data.' });
              } else if (hasOutOfBufferCells(cellSelection.selectedCells, copyOffset, copyRows.length)) {
                toast.default({ title: 'Partial copy', description: 'Copied from loaded rows. Use Export for full dataset.' });
              }
            })
            .finally(() => setIsCopying(false));
        } else {
          const text = buildCopyText(cellSelection.selectedCells, copyRows, columnIds, colKeyMap, copyOffset, spatialCopyTransform);
          const html = buildCopyHtml(cellSelection.selectedCells, copyRows, columnIds, colNames, colKeyMap, copyOffset, spatialCopyTransform);
          void writeRichToClipboard(text, html);
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [cellSelection.selectedCells, sortedResults, normalizedColumns, isWindowed, wdBufferRows, wdBufferStart, getCellValueFn, isCopying, sourceGeneration, spatialCopyTransform]);

  const exportableResultSets = useMemo<QueryResultSet[]>(() => {
    if (resultSets && resultSets.length > 0) return resultSets;

    const hasLegacyData = results.length > 0 || columns.length > 0;
    if (!hasLegacyData) return [];

    const inferColumnNames = (): string[] => {
      if (columns.length > 0) {
        return columns.map((col, index) => col.name || col.id || `Column ${index + 1}`);
      }

      const firstRow = results[0];
      if (Array.isArray(firstRow)) {
        const maxLen = Math.max(0, ...results.map((row) => (Array.isArray(row) ? row.length : 0)));
        return Array.from({ length: maxLen }, (_v, index) => `Column ${index + 1}`);
      }

      if (firstRow && typeof firstRow === 'object') {
        return Object.keys(firstRow as Record<string, unknown>);
      }

      return [];
    };

    const columnNames = inferColumnNames();
    const derivedColumns = columnNames.map((name) => ({ name }));

    if (results.length === 0) {
      return [{ columns: derivedColumns, rows: [], rowCount: 0 }];
    }

    const firstRow = results[0];
    if (Array.isArray(firstRow)) {
      const rows = results.map((row) => {
        if (!Array.isArray(row)) return row as Record<string, unknown>;
        const obj: Record<string, unknown> = {};
        columnNames.forEach((colName, index) => {
          obj[colName] = row[index];
        });
        return obj;
      });

      return [{ columns: derivedColumns, rows: rows as Record<string, unknown>[], rowCount: rows.length }];
    }

    return [{ columns: derivedColumns, rows: results as Record<string, unknown>[], rowCount: results.length }];
  }, [columns, resultSets, results]);

  const canExport = exportableResultSets.length > 0;
  const activeExportResultSetIndex = Math.min(activeResultSetIndex, Math.max(0, exportableResultSets.length - 1));

  const isPlaintext = displayMode === 'plaintext';
  const isStackView = viewMode === 'stack';
  const isWindowedMultiResultSet = isWindowed && Boolean(hasMultipleResultSets);
  const showOutputTab = hasUserPrintMessages(messages) && Boolean(hasMultipleResultSets);
  const handleSelectResultSetFromOutput = useCallback((index: number) => {
    setIsOutputActive(false);
    setViewMode('tabs');
    setActiveResultSetIndex(index);
  }, []);
  const plaintextRowCounts = useMemo(
    () => exportableResultSets.map((rs) => rs.rowCount ?? (Array.isArray(rs.rows) ? rs.rows.length : 0)),
    [exportableResultSets],
  );
  // Fan-out fetches rows for every cached result set so plaintext mode and
  // stacked view can render them all without each spinning up its own buffer.
  const plaintextRowsByIndex = useWindowedPlaintextRows({
    enabled: isWindowedMultiResultSet && (isPlaintext || isStackView),
    cacheUris: resultSetCacheUris,
    rowCounts: plaintextRowCounts,
    cacheGeneration,
  });

  const {
    handleExport, handleCopy, isLocalExporting, copied,
    showExportLimitDialog, setShowExportLimitDialog, exportLimitRowCount,
  } = useExportCopy({
    exportableResultSets,
    activeExportResultSetIndex,
    canExport,
    canExportFull,
    engineId,
    isWindowed,
    wdBufferRows,
    wdBufferStart,
    activeResultSetColumns: activeResultSet?.columns ?? [],
    effectiveTotalRowCount,
    getCellValueFn,
    sourceGeneration,
    // T5 — WKT copy/export parity: file export (CSV/XLSX) and clipboard copy
    // (all 4 formats) decode native-spatial columns to WKT text.
    nativeSpatialColumnIds,
    // D2 — identity lookup for generated INSERTs (Copy INSERT / Export SQL).
    connectionId,
    database,
  });
  const isExporting = isLocalExporting || isWindowedExporting;

  const handleToggleRowSidePanel = useCallback(() => {
    setIsRowSidePanelOpen((isOpen) => {
      const next = !isOpen;
      if (next) {
        setSelectedRowIndex((current) => {
          if (current !== null) return current;
          if (sortedResults.length === 0) return null;
          selectedRowRef.current = sortedResults[0] ?? null;
          return 0;
        });
      }
      return next;
    });
  }, [sortedResults]);

  // Slice 2 (Pass 2): Native JSON columns (e.g. PG `jsonb`, MySQL `json`,
  // MSSQL `json`) and user-declared JSON columns must surface the JSON cell
  // affordance in Query Editor results — same parity as Table Explorer.
  // Heuristic detection alone misses native types because their values arrive
  // as parsed JS objects (so `looksLikeJsonString` never fires) and their
  // dataType isn't a string type. Categorize per column using the source
  // schema/table the column came from.
  const forcedJsonColumnIds = useMemo(() => {
    const ids = new Set<string>();
    // E1 column-metadata path: an explicit non-`not-json` schema
    // classification (`native-jsonb` / `native-json`) authoritatively
    // forces JSON cell rendering regardless of engine, source-table
    // metadata, value size, or the value-shape heuristic.
    for (const column of normalizedColumns) {
      if (isJsonClassifiedColumn(column.jsonClassification)) {
        ids.add(column.id);
      }
    }
    if (!validEngine) return ids;
    for (const column of normalizedColumns) {
      if (ids.has(column.id)) continue;
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: JsonColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
      };
      const cat = categorizeJsonColumn(input, metaInfo);
      if (cat === 'native-json' || cat === 'declared-json') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns]);

  const declaredJsonColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: JsonColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
      };
      if (categorizeJsonColumn(input, metaInfo) === 'declared-json') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns]);

  const nativeJsonColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: JsonColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
      };
      if (categorizeJsonColumn(input, metaInfo) === 'native-json') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns]);

  const jsonColumnIdSet = useMemo(() => {
    const jsonColumns = new Set<string>(forcedJsonColumnIds);

    for (const column of normalizedColumns) {
      if (jsonColumns.has(column.id)) continue;
      const shouldCheckByType = isStringDataType(column.dataType);
      const shouldCheckUnknown = !column.dataType;

      if (!shouldCheckByType && !shouldCheckUnknown) continue;
      if (guessJsonColumn(normalizedResults, column.key, { maxParseAttempts: 8, minSuccessfulParses: 2 })) {
        jsonColumns.add(column.id);
      }
    }

    return jsonColumns;
  }, [forcedJsonColumnIds, normalizedColumns, normalizedResults]);

  // Columns that look like JSON but aren't declared yet — surfaces the
  // "Declare as JSON" entry in the column-header and cell context menus.
  // Requires a known (engine, schema, table, column) so the declaration can
  // be persisted against MetaInfo, so query results that lack source-column
  // metadata (e.g. computed columns) are silently skipped.
  const declarableJsonColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: JsonColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
      };
      if (categorizeJsonColumn(input, metaInfo) !== 'json-eligible') continue;
      if (!guessJsonColumn(normalizedResults, column.key, { maxParseAttempts: 8, minSuccessfulParses: 2 })) continue;
      ids.add(column.id);
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns, normalizedResults]);

  // Enum-column categorization parity with TableExplorerGrid. The same
  // three Sets (declared / native / eligible) drive the header glyph.
  const declaredEnumColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: EnumColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
        isNativeEnumType: nativeEnumSourceColumnIds.has(column.id),
      };
      if (categorizeEnumColumn(input, metaInfo) === 'declared-enum') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns, nativeEnumSourceColumnIds]);

  const nativeEnumColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: EnumColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
        isNativeEnumType: nativeEnumSourceColumnIds.has(column.id),
      };
      if (categorizeEnumColumn(input, metaInfo) === 'native-enum') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns, nativeEnumSourceColumnIds]);

  // Broad declarable set: any sensibly-typed column (short text / integer),
  // matching the "Declare as enum" right-click entry. The name-hint gate
  // (`hintedEnumColumnIds` below) controls the inline ▾? glyph separately;
  // this set intentionally stays broad so the context menu is always reachable.
  // The lookup-enum resolver's per-column target — one unconditional FK target
  // (real, or a single unfiltered non-path loose rel), or absent. Shared by the
  // declarable set, the hint's FK arm (both read membership only) and
  // `useLookupLabelMaps`, which needs the target itself to resolve implicit
  // lookup enums via `referenceTables`. Mirrors TableExplorerGrid.
  const fkLookupTargets = useMemo(() => {
    const targets = new Map<string, EnumLookupTarget>();
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn) continue;
      const schema = column.sourceSchema ?? '';
      const realFkTarget = realFkMap.get(resultSetFkKey(column)!);
      const { target } = resolveLookupEnumTarget({
        dataType: column.dataType,
        realFk: realFkTarget
          ? {
              referencedSchema: realFkTarget.schema,
              referencedTable: realFkTarget.table,
              referencedColumn: realFkTarget.column,
            }
          : undefined,
        looseRels: (metaInfo?.looseRelationships ?? []).filter(
          (rel) =>
            rel.fromSchema === schema &&
            rel.fromTable === column.sourceTable &&
            rel.fromColumn === column.sourceColumn,
        ),
      });
      if (target) targets.set(column.id, target);
    }
    return targets;
  }, [normalizedColumns, realFkMap, metaInfo]);

  // R4 — columns with NO declaration of their own whose FK target is a table
  // the user flagged in `referenceTables`. "Mark `dbo.Statuses` once and every
  // column referencing it shows names": an implicit lookup enum is
  // declared-LIKE everywhere (blue `▾`, "Enum details", labelled cell editor),
  // never hint-like. Resolved through the shared
  // `resolveEffectiveLookupDeclaration` seam so it can't drift from the label
  // maps / picker / editor. Mirrors TableExplorerGrid.
  //
  // `'cached'` mode ONLY. An on-demand target's pairs are never enumerable —
  // the whole point is that the table is too big — so the surfaces this set
  // feeds (the blue `▾`, "Enum details", the enum cell editor, the enum filter
  // picker) would all be offering a list that cannot be built. Those columns
  // get labels and nothing else; labels and glyphs are driven by two different
  // sets on purpose (design § "Deliberate exclusions").
  const implicitLookupColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!metaInfo || (metaInfo.referenceTables?.length ?? 0) === 0) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn) continue;
      const effective = resolveEffectiveLookupDeclaration({
        metaInfo,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        column: column.sourceColumn,
        resolvedTarget: fkLookupTargets.get(column.id),
      });
      if (effective?.kind === 'implicit' && effective.mode === 'cached') ids.add(column.id);
    }
    return ids;
  }, [metaInfo, normalizedColumns, fkLookupTargets]);

  /**
   * Columns whose pairs are obtained on demand — the implicit ones excluded
   * above PLUS the explicitly declared ones pointing at the same kind of
   * target. An explicit declaration says what the column IS; it never says how
   * a table too big to enumerate gives up its pairs, so it cannot buy back the
   * dropdown-shaped surfaces (B-1). Subtracted from the declared-LIKE union
   * below; the declaration itself stays on disk and wins again the moment the
   * flag goes back to `'cached'`. Mirrors TableExplorerGrid.
   */
  const onDemandLookupColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!metaInfo || (metaInfo.referenceTables?.length ?? 0) === 0) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn) continue;
      const onDemand = isOnDemandLookupColumn({
        metaInfo,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        column: column.sourceColumn,
        resolvedTarget: fkLookupTargets.get(column.id),
      });
      if (onDemand) ids.add(column.id);
    }
    return ids;
  }, [metaInfo, normalizedColumns, fkLookupTargets]);

  /**
   * (Declared ∪ implicit) − on-demand — the set every "this column IS an enum"
   * surface reads (header glyph, header context menu, cell-edit variant).
   * `declaredEnumColumnIds` itself stays literal so nothing that means "has a
   * declaration record" drifts.
   * The union is what preserves mutual exclusivity for free: the glyph and menu
   * chains already test declared FIRST, so an implicit column can never also
   * render the `▾?` hint or the "Declare as enum" entry.
   */
  const enumDeclaredLikeColumnIds = useMemo(() => {
    if (implicitLookupColumnIds.size === 0 && onDemandLookupColumnIds.size === 0) {
      return declaredEnumColumnIds;
    }
    const ids = new Set([...declaredEnumColumnIds, ...implicitLookupColumnIds]);
    for (const id of onDemandLookupColumnIds) ids.delete(id);
    return ids;
  }, [declaredEnumColumnIds, implicitLookupColumnIds, onDemandLookupColumnIds]);

  const declarableEnumColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: EnumColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
        isNativeEnumType: nativeEnumSourceColumnIds.has(column.id),
      };
      // Type-eligible, or a lookup-enum candidate via its FK target — the
      // right-click "Declare as enum" entry must be at least as broad as the
      // `▾?` glyph (see Docs/EnumColumns.md).
      if (isEnumDeclarableType(input) || fkLookupTargets.has(column.id)) {
        ids.add(column.id);
      }
    }
    return ids;
  }, [validEngine, normalizedColumns, nativeEnumSourceColumnIds, fkLookupTargets]);

  // Name-gated subset → the proactive ▾? glyph. Mirrors TableExplorerGrid's
  // hintedEnumColumnIds (enum-eligible ∩ nameSuggestsEnum). Without this the
  // QE ▾? would fire on every short varchar (the documented visual-noise
  // regression). Broad declarableEnumColumnIds stays wired to the context menu.
  const dismissedEnumKeys = useMemo(
    () => buildDismissedEnumKeySet(metaInfo),
    // buildDismissedEnumKeySet reads only metaInfo.enumGlyphDismissals; depending
    // on the whole metaInfo object would needlessly recompute on unrelated
    // metaInfo changes. Narrow dep is intentional and correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metaInfo?.enumGlyphDismissals],
  );

  const hintedEnumColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!validEngine) return ids;
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: EnumColumnInput = {
        engine: validEngine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
        isNativeEnumType: nativeEnumSourceColumnIds.has(column.id),
      };
      if (
        dismissedEnumKeys.has(
          `${column.sourceSchema ?? ''}.${column.sourceTable}.${column.sourceColumn}`,
        )
      )
        continue;
      if (!nameSuggestsEnum(column.sourceColumn)) continue;
      if (categorizeEnumColumn(input, metaInfo) === 'enum-eligible') {
        ids.add(column.id);
        continue;
      }
      // FK arm (parity with `TableExplorerGrid`) — a categorically-named
      // column with one unconditional FK target is hinted even when its type
      // alone isn't eligible. Same resolution `handleEnumGlyphClick` performs,
      // so the glyph always opens in lookup mode.
      if (fkColumnSuggestsEnum(input, metaInfo, fkLookupTargets.has(column.id))) {
        ids.add(column.id);
      }
    }
    return ids;
  }, [validEngine, metaInfo, normalizedColumns, dismissedEnumKeys, nativeEnumSourceColumnIds, fkLookupTargets]);

  // Hoisted directly above handleEnumGlyphClick to satisfy TS2448 — this
  // useAnalytics destructure must precede track's first use in that callback.
  const { track } = useAnalytics();

  const handleEnumGlyphClick = useCallback(
    (columnId: string, mode: 'native' | 'eligible' | 'declared') => {
      if (!validEngine || !connectionId || !database) return;
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column?.sourceTable || !column.sourceColumn) return;
      track('feature:enum_glyph_click', { source: mode, surface: analyticsSurface ?? 'query_editor' });
      const schema = column.sourceSchema ?? '';
      if (mode === 'declared') {
        const decl = metaInfo?.enumColumns.find(
          (d) =>
            d.schema === schema &&
            d.table === column.sourceTable &&
            d.column === column.sourceColumn,
        );
        // An IMPLICIT lookup enum has no declaration record at all — the blue
        // glyph it renders still has to open details. `'lookup'` is the only
        // source it can have (R5 gives the dialog its implicit variant).
        if (!decl && !implicitLookupColumnIds.has(columnId)) return;
        openEnumDetailsDialog({
          connectionId,
          database,
          schema,
          table: column.sourceTable,
          column: column.sourceColumn,
          engine: validEngine,
          declaredSource: decl?.source ?? 'lookup',
          source: 'header-glyph',
        });
        return;
      }
      // Lookup-enum resolution — parity with `TableExplorerGrid`. The real FK
      // comes from the result-set FK map (source-table constraints), the loose
      // candidates from MetaInfo on the same source triple.
      const realFkTarget = realFkMap.get(resultSetFkKey(column)!);
      const resolution =
        mode === 'eligible'
          ? resolveLookupEnumTarget({
              dataType: column.dataType,
              realFk: realFkTarget
                ? {
                    referencedSchema: realFkTarget.schema,
                    referencedTable: realFkTarget.table,
                    referencedColumn: realFkTarget.column,
                  }
                : undefined,
              looseRels: (metaInfo?.looseRelationships ?? []).filter(
                (rel) =>
                  rel.fromSchema === schema &&
                  rel.fromTable === column.sourceTable &&
                  rel.fromColumn === column.sourceColumn,
              ),
            })
          : null;
      const lookupTarget = resolution?.target ?? undefined;
      const lookupIneligibleReason =
        resolution && resolution.target === null && resolution.reason !== 'none'
          ? resolution.reason
          : undefined;
      openDeclareEnumColumnDialog({
        connectionId,
        database,
        schema,
        table: column.sourceTable,
        column: column.sourceColumn,
        dataType: column.dataType,
        engine: validEngine,
        mode: lookupTarget ? 'lookup' : mode === 'native' ? 'native' : 'eligible',
        trigger: 'header-glyph',
        ...(lookupTarget ? { lookupTarget } : {}),
        ...(lookupIneligibleReason ? { lookupIneligibleReason } : {}),
      });
    },
    [validEngine, connectionId, database, normalizedColumns, metaInfo, realFkMap, track, analyticsSurface, implicitLookupColumnIds],
  );

  const handleSpatialColumnAction = useCallback(
    (columnId: string, state: 'declared' | 'declarable', triggerOverride?: DeclareSpatialColumnDialogTrigger) => {
      if (!validEngine || !connectionId || !database) return;
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column?.sourceTable || !column.sourceColumn) return;
      const schema = column.sourceSchema ?? '';
      // Sibling numeric columns from the SAME source table — candidate LNG
      // columns for a lat-lng pair. Exclude this column itself.
      const siblingColumnsRaw = normalizedColumns
        .filter(
          (c) =>
            c.id !== columnId &&
            c.sourceTable === column.sourceTable &&
            (c.sourceSchema ?? '') === schema &&
            !!c.sourceColumn,
        )
        .map((c) => c.sourceColumn as string);
      // T19 — when the column was suggested via the pair NAME arm (e.g.
      // `pickup_lat`), put its best-guess LNG partner first so the dialog's
      // default "Longitude column" selection (`siblingColumns[0]`) is right,
      // not whatever column happens to be first in the table.
      const partner =
        state === 'declarable' ? findSpatialPairPartner(column.sourceColumn, siblingColumnsRaw) : null;
      const siblingColumns = partner
        ? [partner, ...siblingColumnsRaw.filter((c) => c !== partner)]
        : siblingColumnsRaw;
      openDeclareSpatialColumnDialog({
        connectionId,
        database,
        schema,
        table: column.sourceTable,
        column: column.sourceColumn,
        dataType: column.dataType,
        engine: validEngine,
        trigger: triggerOverride ?? (state === 'declared' ? 'glyph' : 'header-menu'),
        siblingColumns,
        existing: state === 'declared' ? declaredSpatialColumns.get(columnId) : undefined,
      });
    },
    [validEngine, connectionId, database, normalizedColumns, declaredSpatialColumns],
  );

  const handleDeclareAsJson = useCallback(
    (columnId: string, source: 'header-menu' | 'cell-menu') => {
      if (!validEngine || !connectionId || !database) return;
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column?.sourceTable || !column.sourceColumn) return;
      const sampleValues = normalizedResults.map((row) => row[column.key]);
      openDeclareJsonColumnDialog({
        connectionId,
        database,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        column: column.sourceColumn,
        dataType: column.dataType,
        engine: validEngine,
        sampleValues,
        source,
      });
    },
    [validEngine, connectionId, database, normalizedColumns, normalizedResults],
  );

  const handleShowJsonDetails = useCallback(
    (columnId: string, source: 'header-menu' | 'cell-menu') => {
      if (!validEngine || !connectionId || !database) return;
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column?.sourceTable || !column.sourceColumn) return;
      openJsonInspectorDialog({
        connectionId,
        database,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        column: column.sourceColumn,
        engine: validEngine,
        dataType: column.dataType,
        source,
      });
    },
    [validEngine, connectionId, database, normalizedColumns],
  );

  // Column IDs that already host at least one loose FK on the source triple
  // — drives the "Edit loose relationship" context-menu item.
  const looseFkColumnIds = useMemo(() => {
    const ids = new Set<string>();
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn) continue;
      if (
        looseFkLookup.has(
          `${column.sourceSchema ?? ''}|${column.sourceTable}|${column.sourceColumn}`,
        )
      ) {
        ids.add(column.id);
      }
    }
    return ids;
  }, [normalizedColumns, looseFkLookup]);

  // Column IDs eligible to declare a (first) loose FK on — has source coords,
  // is not already a real FK (those don't benefit from a loose declaration in
  // practice), and isn't already loose. Mirrors how `declarableJsonColumnIds`
  // gates the "Declare as JSON" entry.
  const looseFkEligibleColumnIds = useMemo(() => {
    const ids = new Set<string>();
    for (const column of normalizedColumns) {
      if (!column.sourceTable || !column.sourceColumn) continue;
      if (column.isForeignKey) continue;
      if (looseFkColumnIds.has(column.id)) continue;
      // Datetime / boolean columns are never meaningful FK sources — hide the
      // "declare loose FK" affordance for them (editing an existing one stays
      // available via `looseFkColumnIds`).
      if (!isLooseFkCandidateType(column.dataType)) continue;
      ids.add(column.id);
    }
    return ids;
  }, [normalizedColumns, looseFkColumnIds]);

  const handleEditLooseFkFromMenu = useCallback(
    (columnId: string, surface: 'header-menu' | 'cell-menu') => {
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column || !connectionId || !database) return;
      if (!column.sourceTable || !column.sourceColumn) return;
      const rel = (metaInfo?.looseRelationships ?? []).find(
        (r) =>
          r.fromSchema === (column.sourceSchema ?? '') &&
          r.fromTable === column.sourceTable &&
          r.fromColumn === column.sourceColumn,
      );
      if (!rel) return;
      track('feature:loose_fk_context_menu_edit', {
        surface: analyticsSurface ?? 'query_editor',
        from: surface,
      });
      openLooseRelationshipEditorDialog({
        connectionId,
        database,
        engine: validEngine ?? undefined,
        source: {
          schema: column.sourceSchema ?? '',
          table: column.sourceTable,
          name: column.sourceColumn,
          dataType: column.dataType ?? '',
        },
        existingRelationshipId: rel.id,
        source_label: `context_menu_${surface === 'header-menu' ? 'header' : 'cell'}`,
      });
    },
    [normalizedColumns, connectionId, database, validEngine, metaInfo, track, analyticsSurface],
  );

  const handleDeclareLooseFkFromMenu = useCallback(
    (columnId: string, surface: 'header-menu' | 'cell-menu') => {
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column || !connectionId || !database) return;
      if (!column.sourceTable || !column.sourceColumn) return;
      track('feature:loose_fk_context_menu_declare', {
        surface: analyticsSurface ?? 'query_editor',
        from: surface,
      });
      openLooseRelationshipEditorDialog({
        connectionId,
        database,
        engine: validEngine ?? undefined,
        source: {
          schema: column.sourceSchema ?? '',
          table: column.sourceTable,
          name: column.sourceColumn,
          dataType: column.dataType ?? '',
        },
        // No existingRelationshipId → dialog opens in add-another mode.
        source_label: `context_menu_${surface === 'header-menu' ? 'header' : 'cell'}_declare`,
      });
    },
    [normalizedColumns, connectionId, database, validEngine, track, analyticsSurface],
  );

  const handleDeclaredGlyphClick = useCallback((columnId: string) => {
    const column = normalizedColumns.find((c) => c.id === columnId);
    if (!column || !validEngine || !connectionId || !database) return;
    if (!column.sourceTable || !column.sourceColumn) return;
    track('feature:json_glyph_click', {
      source: 'declared',
      surface: analyticsSurface ?? 'query_editor',
    });
    openJsonInspectorDialog({
      connectionId,
      database,
      schema: column.sourceSchema ?? '',
      table: column.sourceTable,
      column: column.sourceColumn,
      engine: validEngine,
      dataType: column.dataType,
      source: 'header-glyph',
    });
  }, [normalizedColumns, validEngine, connectionId, database, track, analyticsSurface]);

  const handleNativeOrDeclarableGlyphClick = useCallback(
    (columnId: string, source: 'native' | 'eligible') => {
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column || !validEngine || !connectionId || !database) return;
      if (!column.sourceTable || !column.sourceColumn) return;
      track('feature:json_glyph_click', {
        source,
        surface: analyticsSurface ?? 'query_editor',
      });
      const sampleValues = normalizedResults.map((row) => row[column.key]);
      openDeclareJsonColumnDialog({
        connectionId,
        database,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        column: column.sourceColumn,
        dataType: column.dataType,
        engine: validEngine,
        sampleValues,
        source: 'header-glyph',
        mode: source === 'native' ? 'native' : 'eligible',
      });
    },
    [normalizedColumns, validEngine, connectionId, database, normalizedResults, track, analyticsSurface],
  );

  const handleNativeGlyphClick = useCallback(
    (id: string) => handleNativeOrDeclarableGlyphClick(id, 'native'),
    [handleNativeOrDeclarableGlyphClick],
  );
  const handleDeclarableGlyphClick = useCallback(
    (id: string) => handleNativeOrDeclarableGlyphClick(id, 'eligible'),
    [handleNativeOrDeclarableGlyphClick],
  );

  // Spatial header glyph. A native glyph records intent (the map peek is the
  // cell-level interaction); a declared glyph (T18) opens the coordinate
  // details/remove dialog. Analytics mirror the JSON/enum glyph events
  // (feature:*_glyph_click); props are categorical only.
  const handleSpatialGlyphClick = useCallback(
    (columnId: string, state: SpatialGlyphState) => {
      track('feature:spatial_glyph_click', {
        source: state,
        surface: analyticsSurface ?? 'query_editor',
      });
      if (state === 'declared') handleSpatialColumnAction(columnId, 'declared');
      // T19 — the `⌖?` glyph opens the declare dialog pre-filled, mirroring the
      // enum `hintedEnumColumnIds` glyph flow. Explicit 'glyph' trigger (rather
      // than the header-menu default) so `feature:spatial_declare`'s trigger
      // prop correctly attributes this NEW entry point.
      else if (state === 'declarable') handleSpatialColumnAction(columnId, 'declarable', 'glyph');
    },
    [track, analyticsSurface, handleSpatialColumnAction],
  );

  const handleLooseFkGlyphClick = useCallback(
    (columnId: string) => {
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column || !connectionId || !database) return;
      if (!column.sourceTable || !column.sourceColumn) return;
      // First matching loose FK on the source triple — the editor opens
      // pre-filled against it. Multi-FK source columns can still surface
      // additional declarations via the dialog's "Add another" button.
      const rel = (metaInfo?.looseRelationships ?? []).find(
        (r) =>
          r.fromSchema === (column.sourceSchema ?? '') &&
          r.fromTable === column.sourceTable &&
          r.fromColumn === column.sourceColumn,
      );
      if (!rel) return;
      track('feature:loose_fk_header_glyph_click', {
        surface: analyticsSurface ?? 'query_editor',
      });
      openLooseRelationshipEditorDialog({
        connectionId,
        database,
        engine: validEngine ?? undefined,
        source: {
          schema: column.sourceSchema ?? '',
          table: column.sourceTable,
          name: column.sourceColumn,
          dataType: column.dataType ?? '',
        },
        existingRelationshipId: rel.id,
        source_label: 'header_glyph',
      });
    },
    [normalizedColumns, connectionId, database, validEngine, metaInfo, track, analyticsSurface],
  );

  const openExpandedTextDialog = useCallback((
    title: string, value: string, columnId?: string, rowData?: Record<string, unknown>,
    sourceRowIndex?: number, spatial?: ExpandedSpatialPayload,
  ) => {
    const mode = detectContentMode(value);
    setExpandedTextDialog({ title, value, mode, columnId, rowData, spatial });
    // In windowed mode, the grid cell value may be a truncated 1KB preview.
    // Fetch the full untruncated value from the backend source asynchronously.
    if (isWindowed && cacheUri && columnId != null && sourceRowIndex != null) {
      setExpandedTextDialog((prev) => prev ? { ...prev, isLoadingFullValue: true } : prev);
      getResultCacheCellValue({ uri: cacheUri, rowIndex: sourceRowIndex, columnId, expectedGeneration: sourceGeneration })
        .then((response) => {
          if (response.error) {
            toast.default({ title: 'Cell fetch failed', description: response.error });
            setExpandedTextDialog((prev) => prev ? { ...prev, isLoadingFullValue: false } : prev);
            return;
          }
          const fullValue = response.value == null ? '' : (typeof response.value === 'object' ? JSON.stringify(response.value) : String(response.value));
          setExpandedTextDialog((prev) => prev ? { ...prev, value: fullValue, mode: detectContentMode(fullValue), isLoadingFullValue: false } : prev);
        })
        .catch(() => {
          setExpandedTextDialog((prev) => prev ? { ...prev, isLoadingFullValue: false } : prev);
        });
    }
  }, [isWindowed, cacheUri]);

  const columnIds = useMemo(() => normalizedColumns.map((c) => c.id), [normalizedColumns]);
  const {
    columnPinning,
    onColumnPinningChange,
    pinColumn,
    unpinColumn,
    isColumnPinned,
    getPinnedLeftOffset,
    getPinnedRightOffset,
    isLastPinnedLeft,
    isFirstPinnedRight,
  } = useColumnPinning({ columnIds });

  // Lookup-enum labels for FK cells (`3 · Active`). Read straight off the
  // MetaInfo snapshot — no per-cell query — and threaded into the cell
  // renderer as pure presentation. Mirrored in `TableExplorerGrid`. The
  // on-demand half and the merge sit further down, next to the visible window
  // they need.
  const cachedLookupLabelInfos = useLookupLabelMaps({
    metaInfo,
    columns: normalizedColumns,
    fkLookupTargets,
  });

  const columnResizeMode: ColumnResizeMode = 'onChange';

  // Track container height for virtualization
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => setContainerHeight(container.clientHeight);
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  // Detect the post-save data refresh so the side panel & selected-row index
  // survive the implicit re-execute. Without this the user loses their place
  // every time they hit Save in edit mode — same rows come back, but the
  // panel closes. The save itself flips `editMode.isSaving` true→false right
  // before triggering the re-execute, so a transition watcher can arm a
  // one-shot "preserve next refresh" flag that the reset effect consumes.
  const preserveSelectionOnNextRefreshRef = useRef(false);
  const prevIsSavingRef = useRef(false);
  useEffect(() => {
    const nowSaving = editMode?.isSaving ?? false;
    if (prevIsSavingRef.current && !nowSaving) {
      preserveSelectionOnNextRefreshRef.current = true;
    }
    prevIsSavingRef.current = nowSaving;
  }, [editMode?.isSaving]);

  // Reset scroll/selection when results, active result set, or windowed data version change.
  // `cacheGeneration` covers the window→inline mode swap on re-execute (new query):
  // without it, the previous query's scrollTop persists and virtualization renders
  // empty rows past the new (smaller) result set.
  // useLayoutEffect (not useEffect) so the scroll reset is applied in the same paint
  // as the new data — otherwise React can paint one frame with new rows but stale
  // scrollTop, leaving virtualization computing start > end and a blank tbody until
  // the next user event (e.g. scroll wheel) forces a re-render.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container) container.scrollTop = 0;
    setScrollTop(0);
    if (preserveSelectionOnNextRefreshRef.current) {
      // Save-driven refresh: keep the panel open + selection where it was,
      // clamping if the new dataset is shorter than the prior index.
      preserveSelectionOnNextRefreshRef.current = false;
      const newLength = remoteTotalRowCount ?? normalizedResults.length;
      setSelectedRowIndex((current) => {
        if (current === null) return current;
        return current < newLength ? current : null;
      });
      cellSelection.clearSelection();
      return;
    }
    setSelectedRowIndex(null);
    setIsRowSidePanelOpen(false);
    selectedRowRef.current = null;
    setEditingCell(null);
    setMenuCell(null);
    setSidePanelEditingColumn(null);
    cellSelection.clearSelection();
  }, [normalizedResults, activeResultSetIndex, dataVersion, sourceGeneration, cacheGeneration]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset sort state on new query execution (not on tab switch)
  useEffect(() => {
    setSortStateByTab({});
  }, [cacheGeneration]);  

  useEffect(() => {
    if (viewMode !== 'stack') return;
    setIsRowSidePanelOpen(false);
  }, [viewMode]);

  // MCP command consumption for query editor tabs
  const getPendingCommands = useQueryEditorMcpStore((state) => state.getPendingCommands);
  const completeCommand = useQueryEditorMcpStore((state) => state.completeCommand);
  const pendingCommandsLength = useQueryEditorMcpStore((state) =>
    tabId ? state.pendingCommands.filter((cmd) => cmd.tabId === tabId).length : 0
  );

  useEffect(() => {
    if (!tabId) return;
    const commands = getPendingCommands(tabId);
    for (const pending of commands) {
      const cmd = pending.command;
      // Only handle and complete commands that ResultsGrid is responsible for
      if (cmd.type === 'select_row') {
        const targetIndex = cmd.resultSetIndex ?? 0;
        if (targetIndex !== activeResultSetIndex) {
          setActiveResultSetIndex(targetIndex);
        }
        if (cmd.rowIndex >= 0 && cmd.rowIndex < (normalizedResults?.length ?? 0)) {
          setSelectedRowIndex(cmd.rowIndex);
          selectedRowRef.current = normalizedResults?.[cmd.rowIndex] as Record<string, unknown> ?? null;
        }
        completeCommand(pending.id);
      } else if (cmd.type === 'show_row_details') {
        if (cmd.show) {
          if (cmd.rowIndex !== undefined && cmd.rowIndex >= 0 && cmd.rowIndex < (normalizedResults?.length ?? 0)) {
            setSelectedRowIndex(cmd.rowIndex);
            selectedRowRef.current = normalizedResults?.[cmd.rowIndex] as Record<string, unknown> ?? null;
          }
          setIsRowSidePanelOpen(true);
        } else {
          setIsRowSidePanelOpen(false);
        }
        completeCommand(pending.id);
      }
      // Other command types (show_chart, set_chart_config, etc.) are handled by QueryEditorTab
    }
  }, [tabId, pendingCommandsLength, getPendingCommands, completeCommand, activeResultSetIndex, normalizedResults]);

  // Cleanup MCP commands on unmount
  useEffect(() => {
    if (!tabId) return;
    return () => {
      useQueryEditorMcpStore.getState().clearTabCommands(tabId);
    };
  }, [tabId]);

  /**
   * Expose this grid's OWN export handlers to `ui_export_results` — the same
   * `handleExport` / `handleExportAllCsv` the toolbar menu calls, so an
   * agent-initiated export cannot drift from the user-initiated one (identity
   * handling, spatial WKT decoding, Personal-mode limits, toasts, analytics).
   * Table Explorer registers its own runner the same way; see
   * `gridExportRegistry.ts` for why the registry is shared.
   */
  useEffect(() => {
    if (!tabId) return;
    return registerGridExport(tabId, {
      exportLoaded: (args) => handleExport(args),
      // Only windowed result sets have a cache to stream from. On a
      // non-windowed set every row is already loaded, so "all rows" and the
      // buffered CSV export are the same file — the tool routes there instead
      // of reporting a capability this grid does not have.
      exportAllCsv: isWindowed ? () => handleExportAllCsv() : undefined,
      loadedRowCount: () => (isWindowed ? wdBufferRows.length : sortedResults.length),
    });
  }, [tabId, handleExport, handleExportAllCsv, isWindowed, wdBufferRows.length, sortedResults.length]);

  const rowSidePanelSplitter = useRowSidePanelResize({
    isOpen: isRowSidePanelOpen,
    viewMode,
    containerRef: resultsSplitRef,
    widthPx: rowSidePanelWidthPx,
    setWidthPx: setRowSidePanelWidthPx,
    resetWidthPx: ROW_SIDE_PANEL_DEFAULT_WIDTH,
    onReset: () => track('feature:splitter_reset', { surface: 'results-row-side-panel' }),
  });

  const isProportionalRef = useRef(false);
  const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const nextScrollTop = event.currentTarget.scrollTop;
    // Update synchronously so skeleton rows appear in the same frame as the
    // scroll — deferring via requestAnimationFrame causes a visible flash of
    // empty space before skeletons render.
    //
    // Quantized to ROW_HEIGHT buckets: the virtualization window only changes
    // when the scroll position crosses a row boundary, so same-bucket updates
    // would re-render the grid for nothing — at trackpad event rates that is
    // dozens of wasted renders per second (React bails out when the returned
    // state is unchanged). Proportional mode keeps pixel precision — there one
    // pixel maps to many rows.
    setScrollTop((prev) => {
      if (isProportionalRef.current) return nextScrollTop;
      return Math.floor(prev / ROW_HEIGHT) === Math.floor(nextScrollTop / ROW_HEIGHT) ? prev : nextScrollTop;
    });
  }, []);

  // Virtual row calculations
  const totalRows = isWindowed ? effectiveTotalRowCount : sortedResults.length;
  const selectedRow = useMemo(() => {
    if (selectedRowIndex === null) return null;
    if (isWindowed) return selectedRowRef.current;
    const row = sortedResults[selectedRowIndex];
    return row ?? null;
  }, [sortedResults, selectedRowIndex, isWindowed]);
  // Use proportional scrolling for large windowed datasets to avoid browser height limits
  const isProportional = isWindowed && needsProportionalScroll(totalRows);
  isProportionalRef.current = isProportional;

  // Fast-scroll deferral: the EXPENSIVE window (the one real rows render
  // from) follows scrollTop through useDeferredValue, so a burst of scroll
  // events (trackpad flick, fast wheel spin) never queues stale full-window
  // renders — React abandons an in-flight deferred render whenever a newer
  // scroll position lands. While the deferred window lags a fast scroll so
  // far that it no longer overlaps the live window (real rows would all be
  // offscreen anyway), the cheap urgent render paints skeleton rows at the
  // live position instead — instant feedback — and the real rows mount ONCE,
  // where the scroll settles, instead of once per stale intermediate window.
  // Ordinary wheel steps keep overlapping windows, so they never see
  // skeletons — overscan covers the 1–3 row gap until the deferred render
  // lands a frame later.
  const deferredScrollTop = useDeferredValue(scrollTop);

  const computeWindow = useCallback((scrollPos: number) => {
    if (!totalRows) {
      return { startIndex: 0, endIndex: 0, topPaddingHeight: 0, bottomPaddingHeight: 0 };
    }

    if (isProportional) {
      return getVirtualPadding(scrollPos, totalRows, containerHeight || 600, DEFAULT_OVERSCAN);
    }

    // Normal mode — direct pixel mapping
    const effectiveHeight = containerHeight || 600;
    const visibleStart = Math.floor(scrollPos / ROW_HEIGHT);
    const visibleEnd = Math.ceil((scrollPos + effectiveHeight) / ROW_HEIGHT);

    const start = Math.max(0, visibleStart - DEFAULT_OVERSCAN);
    const end = Math.min(totalRows, visibleEnd + DEFAULT_OVERSCAN);

    return {
      startIndex: start,
      endIndex: end,
      topPaddingHeight: start * ROW_HEIGHT,
      bottomPaddingHeight: Math.max(0, (totalRows - end) * ROW_HEIGHT),
    };
  }, [containerHeight, totalRows, isProportional]);

  const { startIndex, endIndex, topPaddingHeight, bottomPaddingHeight } = useMemo(
    () => computeWindow(deferredScrollTop),
    [computeWindow, deferredScrollTop]
  );

  // Live (urgent) window — where the user actually is right now. Null while
  // the deferred window is caught up (the common, settled case).
  const urgentWindow = useMemo(
    () => (deferredScrollTop === scrollTop ? null : computeWindow(scrollTop)),
    [computeWindow, scrollTop, deferredScrollTop]
  );

  // Fast-scroll gap: the live window shares no rows with the lagging deferred
  // window — paint skeletons at the live position instead of offscreen rows.
  const fastScrollGapWindow =
    urgentWindow && (urgentWindow.startIndex >= endIndex || urgentWindow.endIndex <= startIndex)
      ? urgentWindow
      : null;

  // Trimmed skeleton window for the gap branch: while skeletons are showing,
  // the scroll is still in flight — overscan rows would only make the cheap
  // urgent commit less cheap. Trims up to the overscan from each side and
  // moves the trimmed height into the paddings, so scroll geometry is exact.
  const gapSkeletonWindow = useMemo(() => {
    if (!fastScrollGapWindow) return null;
    return trimGapSkeletonWindow(fastScrollGapWindow, containerHeight || 600, ROW_HEIGHT, DEFAULT_OVERSCAN);
  }, [fastScrollGapWindow, containerHeight]);

  // Keyboard navigation for proportional scroll mode
  const onGridKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isProportional) return;
    const container = containerRef.current;
    if (!container) return;

    const effectiveHeight = containerHeight || 600;
    const visibleRows = Math.ceil(effectiveHeight / ROW_HEIGHT);
    const currentRow = scrollTopToRowIndex(scrollTop, totalRows, effectiveHeight);
    let targetRow: number | null = null;

    switch (event.key) {
      case 'ArrowDown':
        targetRow = Math.min(currentRow + 1, totalRows - visibleRows);
        break;
      case 'ArrowUp':
        targetRow = Math.max(currentRow - 1, 0);
        break;
      case 'PageDown':
        targetRow = Math.min(currentRow + visibleRows, totalRows - visibleRows);
        break;
      case 'PageUp':
        targetRow = Math.max(currentRow - visibleRows, 0);
        break;
      case 'Home':
        targetRow = 0;
        break;
      case 'End':
        targetRow = totalRows - visibleRows;
        break;
      default:
        return;
    }

    event.preventDefault();
    const newScrollTop = rowIndexToScrollTop(targetRow, totalRows, effectiveHeight);
    container.scrollTop = newScrollTop;
    setScrollTop(newScrollTop);
  }, [isProportional, containerHeight, scrollTop, totalRows]);

  // Report scroll position to parent for status bar display
  const firstVisibleRow = useMemo(() => {
    if (!totalRows) return 0;
    if (isProportional) {
      return scrollTopToRowIndex(scrollTop, totalRows, containerHeight || 600);
    }
    return Math.floor(scrollTop / ROW_HEIGHT);
  }, [scrollTop, totalRows, isProportional, containerHeight]);

  useEffect(() => {
    onScrollPositionChange?.(firstVisibleRow);
  }, [firstVisibleRow, onScrollPositionChange]);

  // Notify windowed data hook of viewport changes for prefetching — driven by
  // the LIVE window so fetches start where the user is heading, not where the
  // deferred render is lagging.
  const { onViewportChange: windowViewportChange, getVisibleRows: windowGetRows, getSkeletonRanges } = windowedData;
  const prefetchStartIndex = urgentWindow?.startIndex ?? startIndex;
  const prefetchEndIndex = urgentWindow?.endIndex ?? endIndex;
  useEffect(() => {
    if (isWindowed) {
      windowViewportChange(prefetchStartIndex, prefetchEndIndex);
    }
  }, [isWindowed, prefetchStartIndex, prefetchEndIndex, windowViewportChange]);

  const visibleResults = useMemo(
    () => isWindowed
      ? windowGetRows(startIndex, endIndex)
      : sortedResults.slice(startIndex, endIndex),
    [isWindowed, windowGetRows, endIndex, sortedResults, startIndex]
  );

  // A visible key that isn't in the cached pairs proves the lookup cache is
  // stale — refresh it once in the background (throttled per column) so the
  // label fills in. Mirrored in `TableExplorerGrid`.
  useLookupLabelSelfHeal({
    connectionId,
    database,
    engine: validEngine ?? undefined,
    metaInfo,
    lookupLabelInfos: cachedLookupLabelInfos,
    columns: normalizedColumns,
    rows: visibleResults,
  });

  // The other half of the label pipeline, for targets flagged
  // `mode: 'on-demand'`: the same visible window, resolved by small batched
  // queries and never written to MetaInfo. `useLookupLabelMaps` emits nothing
  // for those columns and this emits nothing for cached ones, so the two maps
  // are disjoint by construction — the merge below can't shadow either.
  const onDemandLookupLabelInfos = useOnDemandLookupLabels({
    connectionId,
    database,
    metaInfo,
    fkLookupTargets,
    columns: normalizedColumns,
    rows: visibleResults,
  });

  // ONE map for the cells: the suffix, its tooltip, the copy-raw invariant and
  // the edit-mode rendering all ride it without knowing which half a label
  // came from.
  const lookupLabelInfos = useMemo(() => {
    if (onDemandLookupLabelInfos.size === 0) return cachedLookupLabelInfos;
    return new Map([...cachedLookupLabelInfos, ...onDemandLookupLabelInfos]);
  }, [cachedLookupLabelInfos, onDemandLookupLabelInfos]);

  // Built HERE, below the visible window, because that window is what the
  // on-demand half resolves from — the merged map has to exist before the
  // renderer that reads it. (`columnDefs` is only consumed by `useReactTable`
  // further down, so nothing between there and here needs it.)
  const columnDefs = useCellRenderer({
    normalizedColumns,
    sortState,
    jsonColumnIdSet,
    forcedJsonColumnIds,
    declaredJsonColumnIds,
    nativeJsonColumnIds,
    declarableJsonColumnIds,
    openExpandedTextDialog,
    isColumnPinned,
    unpinColumn,
    showColumnProfiling,
    columnProfiles,
    rows: showColumnProfiling ? normalizedResults : EMPTY_ROWS,
    connectionId,
    database,
    editModeRef: editModeRef as React.RefObject<EditModeSnapshot | undefined>,
    minColumnWidth: MIN_COLUMN_WIDTH,
    analyticsSurface,
    onDeclaredGlyphClick: handleDeclaredGlyphClick,
    onNativeGlyphClick: handleNativeGlyphClick,
    onDeclarableGlyphClick: handleDeclarableGlyphClick,
    onLooseFkGlyphClick: handleLooseFkGlyphClick,
    // Declared ∪ implicit — the blue `▾` is the glyph for both.
    declaredEnumColumnIds: enumDeclaredLikeColumnIds,
    nativeEnumColumnIds,
    hintedEnumColumnIds,
    onEnumGlyphClick: handleEnumGlyphClick,
    engine: validEngine ?? undefined,
    nativeSpatialColumnIds,
    declaredSpatialColumnIds,
    declaredSpatialColumns,
    spatialDeclarableColumnIds: declarableSpatialColumnIds,
    onSpatialGlyphClick: handleSpatialGlyphClick,
    getFullSpatialValue,
    numericColumnIds,
    groupedNumericColumnIds,
    numericGrouping,
    lookupLabelInfos,
  });

  // In windowed mode, visibleResults may start after startIndex if the buffer doesn't cover
  // the full viewport. dataStartIndex is the absolute row index of visibleResults[0].
  const dataStartIndex = useMemo(() => {
    if (!isWindowed) return startIndex;
    if (wdBufferEnd <= wdBufferStart) return startIndex; // empty buffer
    return Math.max(startIndex, wdBufferStart);
  }, [isWindowed, startIndex, wdBufferStart, wdBufferEnd]);

  // Compute skeleton row ranges for windowed mode (rows outside buffer that need placeholders)
  const skeletonRanges = useMemo(
    () => isWindowed ? getSkeletonRanges(startIndex, endIndex) : null,
    [isWindowed, getSkeletonRanges, startIndex, endIndex]
  );

  useEffect(() => {
    // In windowed mode, sortedResults is empty — selection is tracked by selectedRowRef + selectedRowIndex directly
    if (isWindowed) return;

    const selectedRow = selectedRowRef.current;
    if (!selectedRow) return;

    const nextIndex = sortedResults.indexOf(selectedRow);
    if (nextIndex === -1) {
      setSelectedRowIndex(null);
      selectedRowRef.current = null;
      return;
    }

    setSelectedRowIndex((current) => (current === nextIndex ? current : nextIndex));
  }, [sortedResults, isWindowed]);

  // Controlled selection (T13 spatial Map view). When the parent DRIVES selection, mirror it into
  // internal state and scroll the row into view — including past the initial window buffer (A21/G5,
  // proportional or pixel mapping). Uncontrolled (no scroll/override) when the prop is omitted.
  useEffect(() => {
    if (externalSelectedRowIndex === undefined) return;
    setSelectedRowIndex(externalSelectedRowIndex);
    if (externalSelectedRowIndex === null) {
      selectedRowRef.current = null;
      return;
    }
    if (!isWindowed) selectedRowRef.current = sortedResults[externalSelectedRowIndex] ?? null;
    const container = containerRef.current;
    if (!container) return;
    const effectiveHeight = containerHeight || 600;
    const target = isProportional
      ? rowIndexToScrollTop(externalSelectedRowIndex, totalRows, effectiveHeight)
      : Math.max(0, externalSelectedRowIndex * ROW_HEIGHT - effectiveHeight / 2 + ROW_HEIGHT / 2);
    container.scrollTop = target;
    setScrollTop(target);
    // Scroll only when the driven selection changes; other deps are read-at-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSelectedRowIndex]);

  // Jump to an absolute row index on demand — the CSV-export skipped-rows
  // toast's "Show row" action (see useWindowedGrid). Same scroll mapping as the
  // controlled-selection effect above, proportional mode included, and it marks
  // the landing row with the grid's EXISTING row-selection highlight rather
  // than inventing a second way to point at a row.
  const jumpToRow = useCallback((rowIndex: number) => {
    if (totalRows <= 0) return;
    const target = Math.max(0, Math.min(rowIndex, totalRows - 1));
    setSelectedRowIndex(target);
    // Windowed rows aren't in `sortedResults` — the row object arrives with the
    // buffer fetch this scroll triggers, and selection is index-tracked there.
    // Dropping the previous row object matters: an open row side panel titled
    // "Row N" would otherwise show the values of whatever row was selected
    // before the jump.
    selectedRowRef.current = isWindowed ? null : (sortedResults[target] ?? null);
    const container = containerRef.current;
    if (!container) return;
    // Measure the panel LIVE: the containerHeight state can be 0/stale at
    // fire time (the toast action can outlive resizes), and the 600px
    // fallback centers the row below the fold of a short results panel —
    // "jumped but nothing visible" defeats the whole action.
    const effectiveHeight = container.clientHeight || containerHeight || 600;
    const scrollTarget = isProportional
      ? rowIndexToScrollTop(target, totalRows, effectiveHeight)
      : Math.max(0, target * ROW_HEIGHT - effectiveHeight / 2 + ROW_HEIGHT / 2);
    container.scrollTop = scrollTarget;
    setScrollTop(scrollTarget);
  }, [totalRows, isWindowed, sortedResults, isProportional, containerHeight]);
  jumpToRowRef.current = jumpToRow;

  // Reset manual column sizing when results change (new query)
  useEffect(() => {
    setColumnSizing((prev) => (Object.keys(prev).length === 0 ? prev : EMPTY_COLUMN_SIZING));
    setIsFixedLayout(false);
  }, [resultSets, results]);

  // Capture current column widths from DOM and switch to fixed layout.
  // Cells are matched by `data-column-id`, not by position: the header row also
  // carries the inert PP-05 filler (and the filter row repeats every column), so
  // an index-based map would hand columns the wrong widths.
  const lockColumnWidths = useCallback(() => {
    if (isFixedLayout) return; // Already locked

    const table = tableRef.current;
    if (!table) return;

    const headerCells = table.querySelectorAll<HTMLElement>('thead th[data-column-id]');
    const newSizing: ColumnSizingState = {};

    headerCells.forEach((cell) => {
      const columnId = cell.dataset.columnId;
      if (columnId) {
        newSizing[columnId] = cell.getBoundingClientRect().width;
      }
    });

    setColumnSizing(newSizing);
    setIsFixedLayout(true);
  }, [isFixedLayout]);

  // ── Memoized-row support ──────────────────────────────────────────────
  // Stable callbacks + ONE ctx object for <ResultsGridDataRow>. During pure
  // scrolling nothing below changes identity, so already-visible rows skip
  // re-rendering entirely (see resultsGridDataRowPropsEqual).
  const onSelectedRowIndexChangeRef = useRef(onSelectedRowIndexChange);
  onSelectedRowIndexChangeRef.current = onSelectedRowIndexChange;

  const handleRowClick = useCallback((actualRowIndex: number, rowData: Record<string, unknown>) => {
    selectedRowRef.current = rowData;
    setSelectedRowIndex(actualRowIndex);
    onSelectedRowIndexChangeRef.current?.(actualRowIndex);
  }, []);

  const handleRowDoubleClick = useCallback((actualRowIndex: number, rowData: Record<string, unknown>) => {
    selectedRowRef.current = rowData;
    setSelectedRowIndex(actualRowIndex);
    onSelectedRowIndexChangeRef.current?.(actualRowIndex);
    setIsRowSidePanelOpen(true);
  }, []);

  const handleStartInlineEdit = useCallback((rowIndex: number, columnId: string) => {
    setEditingCell({ rowIndex, columnId });
  }, []);

  const handleInlineEditCommit = useCallback((rowData: Record<string, unknown>, columnId: string, originalValue: unknown, newValue: unknown) => {
    editModeRef.current?.onCellEdit(rowData, columnId, originalValue, newValue);
    setGridEditVersion((v) => v + 1);
    setEditingCell(null);
  }, []);

  const handleInlineEditCancel = useCallback(() => setEditingCell(null), []);

  // Edit-mode facade for surfaces the grid renders itself (row side panel,
  // expanded-value dialog): identical to the parent's editMode except that
  // onCellEdit also bumps gridEditVersion — re-editing an already-pending
  // cell leaves pendingChangeCount unchanged, so without this bump the
  // memoized rows would keep showing the previous pending value.
  const versionBumpingEditMode = useMemo<ResultsGridEditMode | undefined>(() => {
    if (!editMode) return undefined;
    return {
      ...editMode,
      onCellEdit: (row, columnName, oldValue, newValue) => {
        editMode.onCellEdit(row, columnName, oldValue, newValue);
        setGridEditVersion((v) => v + 1);
      },
    };
  }, [editMode]);

  const getEditModeSnapshot = useCallback(() => editModeRef.current, []);

  const buildSmartContext = useCallback((columnId: string, rowData: Record<string, unknown>) => {
    const col = columnById.get(columnId);
    // Keyed by the source column exactly as the result names it (`resultSetFkKey`).
    const realFkKey = col ? resultSetFkKey(col) : null;
    const realFkTarget = realFkKey ? realFkMap.get(realFkKey) : undefined;
    return (validEngine && connectionId && database && col?.sourceTable)
      ? {
          connectionId,
          database,
          engine: validEngine,
          schema: col.sourceSchema ?? '',
          table: col.sourceTable,
          column: col.sourceColumn ?? col.name,
          // Implicit lookup enums edit exactly like explicit ones: the enum
          // editor with the FK row-picker escape (`resolveCellEditVariant`).
          enumDeclared: enumDeclaredLikeColumnIds.has(columnId),
          enumNative: nativeEnumColumnIds.has(columnId),
          realFkTarget,
          looseRelCandidates: col.sourceColumn
            ? (metaInfo?.looseRelationships ?? []).filter(
                (rel) =>
                  rel.fromSchema === (col.sourceSchema ?? '') &&
                  rel.fromTable === col.sourceTable &&
                  rel.fromColumn === col.sourceColumn,
              )
            : [],
          sourceRow: rowData,
          dataType: col.dataType,
          nullable: col.nullable,
        }
      : undefined;
  }, [columnById, realFkMap, validEngine, connectionId, database, enumDeclaredLikeColumnIds, nativeEnumColumnIds, metaInfo]);

  // Edit-mode paint signal: any change that can alter how a cell RENDERS in
  // edit mode must change this string, because rows read the editMode object
  // through a ref (identity-stable) and only re-render when ctx changes.
  // The per-column editability mask covers `isColumnEditable` re-derivations
  // (e.g. after a metadata refresh) that change cell cursor/editability
  // affordances without moving any of the scalar signals.
  const editableColumnsMask = editMode?.hasEditableColumns
    ? columnIds.map((id) => (editMode.isColumnEditable(id) ? '1' : '0')).join('')
    : '';
  const editSignature = `${editMode?.isEditMode ? 1 : 0}|${editMode?.hasEditableColumns ? 1 : 0}|${editMode?.pendingChangeCount ?? 0}|${editMode?.isSaving ? 1 : 0}|${gridEditVersion}|${editableColumnsMask}`;

  // ── Column widths via <colgroup>, pinned offsets via CSS variables ─────
  // Rows must not depend on `columnSizing`: with per-td widths, every
  // resize-drag mousemove re-rendered all visible rows (the same cost class
  // the row memoization removed from scrolling). Explicit widths now live on
  // <col> elements (fixed layout only — auto layout keeps content sizing),
  // and pinned sticky offsets are CSS custom properties set on the <table>,
  // referenced by var() from the cells — so a resize repaints via CSS with
  // ZERO row re-renders.
  const pinnedStyleByColumnId = useMemo(() => {
    const map = new Map<string, { side: 'left' | 'right'; varName: string; isEdge: boolean }>();
    (columnPinning.left ?? []).forEach((id, i, arr) => {
      map.set(id, { side: 'left', varName: `--grid-pin-l-${i}`, isEdge: i === arr.length - 1 });
    });
    (columnPinning.right ?? []).forEach((id, i) => {
      map.set(id, { side: 'right', varName: `--grid-pin-r-${i}`, isEdge: i === 0 });
    });
    return map;
  }, [columnPinning]);

  const pinnedOffsetVars = useMemo(() => {
    const vars: Record<string, string> = {};
    (columnPinning.left ?? []).forEach((id, i) => {
      vars[`--grid-pin-l-${i}`] = `${getPinnedLeftOffset(id, columnSizing, MIN_COLUMN_WIDTH)}px`;
    });
    (columnPinning.right ?? []).forEach((id, i) => {
      vars[`--grid-pin-r-${i}`] = `${getPinnedRightOffset(id, columnSizing, MIN_COLUMN_WIDTH)}px`;
    });
    return vars;
  }, [columnPinning, columnSizing, getPinnedLeftOffset, getPinnedRightOffset]);

  const rowCtx = useMemo<ResultsGridRowCtx>(() => ({
    columnById,
    numericColumnIds,
    groupedNumericColumnIds,
    numericGrouping,
    minColumnWidth: MIN_COLUMN_WIDTH,
    maxAutoColumnWidth: MAX_AUTO_COLUMN_WIDTH,
    rowHeight: ROW_HEIGHT,
    editSignature,
    pinnedStyleByColumnId,
    isCellSelected: isCellSelectedStable,
    onCellMouseDown: onCellMouseDownStable,
    onCellMouseEnter: onCellMouseEnterStable,
    getEditMode: getEditModeSnapshot,
    onRowClick: handleRowClick,
    onRowDoubleClick: handleRowDoubleClick,
    onStartInlineEdit: handleStartInlineEdit,
    onInlineEditCommit: handleInlineEditCommit,
    onInlineEditCancel: handleInlineEditCancel,
    buildSmartContext,
    onOpenExpandedText: openExpandedTextDialog,
    columnDefs,
  }), [
    columnDefs, columnById, numericColumnIds, groupedNumericColumnIds, numericGrouping, editSignature,
    pinnedStyleByColumnId,
    isCellSelectedStable, onCellMouseDownStable, onCellMouseEnterStable,
    getEditModeSnapshot, handleRowClick, handleRowDoubleClick, handleStartInlineEdit,
    handleInlineEditCommit, handleInlineEditCancel, buildSmartContext, openExpandedTextDialog,
  ]);

  // Which cell the delegated context menu targets, resolved to its row + value.
  const menuRow = menuCell
    ? (isWindowed
        ? (wdBufferRows[menuCell.rowIndex - wdBufferStart] ?? null)
        : (sortedResults[menuCell.rowIndex] ?? null))
    : null;
  const menuColumn = menuCell ? columnById.get(menuCell.columnId) : undefined;
  const menuOriginalValue = menuCell && menuRow ? menuRow[menuColumn?.key ?? menuCell.columnId] : undefined;
  let menuDisplayedValue = menuOriginalValue;
  if (menuCell && menuRow && editMode?.isEditMode) {
    const pk = editMode.getPrimaryKeyForCell(menuRow, menuCell.columnId);
    if (pk) {
      const pending = editMode.getCellPendingValue(pk, menuCell.columnId);
      if (pending !== undefined) menuDisplayedValue = pending;
    }
  }

  // Delegated right-click handler: resolve the target cell from the DOM (every
  // data cell carries `data-row-index`/`data-column-id`) before Radix's own
  // bubble-phase trigger handler opens the menu. Non-cell targets (the PP-05
  // filler, virtualization padding, skeleton rows) suppress the menu.
  const handleGridBodyContextMenuCapture = useCallback((event: React.MouseEvent) => {
    const cellEl = (event.target as HTMLElement).closest?.('td[data-row-index]') as HTMLElement | null;
    if (!cellEl || cellEl.dataset.columnId === undefined) {
      event.stopPropagation();
      return;
    }
    setMenuCell({ rowIndex: Number(cellEl.dataset.rowIndex), columnId: cellEl.dataset.columnId });
  }, []);

  const handleCellMenuOpenChange = useCallback((open: boolean) => {
    if (!open) setMenuCell(null);
  }, []);

  const table = useReactTable({
    data: visibleResults,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_row, index) => String(dataStartIndex + index),
    columnResizeMode,
    enableColumnResizing: true,
    state: {
      columnSizing,
      columnPinning,
    },
    onColumnSizingChange: setColumnSizing,
    onColumnPinningChange,
  });


  // ── Memoized header row ────────────────────────────────────────────────
  // Same treatment as TableExplorerGrid's memoized <thead> (two-grid parity):
  // the header subtree (74-column results: per-column sort/pin/menu chrome +
  // the filter row) depends on nothing scroll-related, so memoizing the
  // ELEMENT lets React bail out of it on scroll-triggered renders — which
  // keeps the URGENT (skeleton) render of the fast-scroll path cheap.
  // `table` is referentially stable, so it can never fire this memo — the
  // headerGroups identity (rebuilt whenever columnDefs change: profiling
  // badges, glyph sets, ...) and the live-resize flag are the real signals.
  const tableHeaderGroups = table.getHeaderGroups();
  const isResizingHeaderColumn = table.getState().columnSizingInfo.isResizingColumn;
  const headerRowContent = useMemo(() => (
              <GridHeaderRow
                table={table}
                columnSizing={columnSizing}
                editMode={editMode ? { isEditMode: editMode.isEditMode, isColumnEditable: editMode.isColumnEditable } : undefined}
                canSort={canSort}
                sortState={sortState}
                setSortState={setSortState}
                isWindowed={isWindowed}
                handleWindowedSortChange={handleWindowedSortChange}
                lockColumnWidths={lockColumnWidths}
                pinColumn={pinColumn}
                unpinColumn={unpinColumn}
                getPinnedLeftOffset={getPinnedLeftOffset}
                getPinnedRightOffset={getPinnedRightOffset}
                isLastPinnedLeft={isLastPinnedLeft}
                isFirstPinnedRight={isFirstPinnedRight}
                showResultFilter={showResultFilter}
                isFilterActive={isFilterActive}
                columnFilters={columnFilters}
                globalFilter={globalFilter}
                setColumnFilters={setColumnFilters}
                setGlobalFilter={setGlobalFilter}
                filterInputRefs={filterInputRefs}
                minColumnWidth={MIN_COLUMN_WIDTH}
                maxAutoColumnWidth={MAX_AUTO_COLUMN_WIDTH}
                numericColumnIds={numericColumnIds}
                isPopulating={isPopulating}
                onAddToFilter={addColumnToFilter ? (columnId) => {
                  const target = resultColumnTarget(columnId);
                  if (target) addColumnToFilter.open(target);
                } : undefined}
                addToFilterUnavailableReason={addColumnToFilter ? (columnId) => {
                  const target = resultColumnTarget(columnId);
                  return target ? addColumnToFilter.unavailableReason(target) : null;
                } : undefined}
                onProfileColumn={(columnId) => {
                  const col = normalizedColumns.find((c) => c.id === columnId);
                  if (!col?.sourceTable || !connectionId) return;
                  openDataProfileTab({
                    connectionId,
                    database: database ?? '',
                    schema: col.sourceSchema ?? '',
                    table: col.sourceTable,
                    focusedColumn: col.sourceColumn ?? col.name,
                    dataMode: 'sample',
                    entryPoint: 'grid_header',
                  });
                }}
                onFindUsages={findUsagesSupported ? (columnId) => {
                  const col = normalizedColumns.find((c) => c.id === columnId);
                  if (!col?.sourceTable || !connectionId) return;
                  openFindUsagesTab({
                    connectionId,
                    database: database ?? '',
                    schema: col.sourceSchema ?? '',
                    table: col.sourceTable,
                    columnName: col.sourceColumn ?? col.name,
                    source: 'query_grid_header',
                  });
                } : undefined}
                declarableJsonColumnIds={declarableJsonColumnIds}
                onDeclareAsJson={(columnId) => handleDeclareAsJson(columnId, 'header-menu')}
                declaredJsonColumnIds={declaredJsonColumnIds}
                onShowJsonDetails={(columnId) => handleShowJsonDetails(columnId, 'header-menu')}
                declarableEnumColumnIds={declarableEnumColumnIds}
                nativeEnumColumnIds={nativeEnumColumnIds}
                declaredEnumColumnIds={enumDeclaredLikeColumnIds}
                onEnumColumnAction={handleEnumGlyphClick}
                declarableSpatialColumnIds={declarableSpatialColumnIds}
                declaredSpatialColumnIds={declaredSpatialColumnIds}
                onSpatialColumnAction={handleSpatialColumnAction}
                looseFkColumnIds={looseFkColumnIds}
                looseFkEligibleColumnIds={looseFkEligibleColumnIds}
                onEditLooseFk={(columnId) => handleEditLooseFkFromMenu(columnId, 'header-menu')}
                onDeclareLooseFk={(columnId) => handleDeclareLooseFkFromMenu(columnId, 'header-menu')}
                onCopyColumnValues={(columnId) => {
                  const col = normalizedColumns.find((c) => c.id === columnId);
                  if (!col) return;
                  const rowModel = table.getRowModel().rows;
                  const values = rowModel.map((r) => spatialCopyTransform((r.original as Record<string, unknown>)[col.key], columnId));
                  const text = columnValuesToCsvList(values);
                  void writeRichToClipboard(text, '');
                  track('feature:results_copy_column_values', {
                    surface: analyticsSurface ?? 'query_editor',
                    count: values.length,
                  });
                  if (isWindowed && effectiveTotalRowCount > rowModel.length) {
                    toast.default({
                      title: 'Partial copy',
                      description: `Copied ${rowModel.length.toLocaleString()} loaded values. Use Export for the full column.`,
                    });
                  }
                }}
              />
  // tableHeaderGroups + isResizingHeaderColumn look "unnecessary" to the lint
  // (the body reads them through the stable `table` instance), but they are
  // the memo's actual invalidation signals — see the comment above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [table, tableHeaderGroups, isResizingHeaderColumn, addColumnToFilter, analyticsSurface, canSort, columnFilters, columnSizing, connectionId, database, declarableEnumColumnIds, declarableJsonColumnIds, declarableSpatialColumnIds, enumDeclaredLikeColumnIds, declaredJsonColumnIds, declaredSpatialColumnIds, editMode, effectiveTotalRowCount, findUsagesSupported, getPinnedLeftOffset, getPinnedRightOffset, globalFilter, handleDeclareAsJson, handleDeclareLooseFkFromMenu, handleEditLooseFkFromMenu, handleEnumGlyphClick, handleShowJsonDetails, handleSpatialColumnAction, handleWindowedSortChange, isFilterActive, isFirstPinnedRight, isLastPinnedLeft, isPopulating, isWindowed, lockColumnWidths, looseFkColumnIds, looseFkEligibleColumnIds, nativeEnumColumnIds, normalizedColumns, numericColumnIds, pinColumn, resultColumnTarget, setColumnFilters, setGlobalFilter, setSortState, showResultFilter, sortState, spatialCopyTransform, track, unpinColumn]);

  // Check if we have any data (from resultSets, legacy results, or windowed cache).
  // A result set with columns but zero rows (e.g. SELECT ... WHERE 1=0) is still
  // "data" — the grid renders headers + an empty body rather than the empty fallback.
  const hasData = isWindowed
    || hasMultipleResultSets
    || (resultSets && resultSets.length > 0 && resultSets.some(rs => {
      const rows = Array.isArray(rs.rows) ? rs.rows : [];
      const cols = Array.isArray(rs.columns) ? rs.columns : [];
      return rows.length > 0 || cols.length > 0;
    })) || results.length > 0;

  const hasOutputOnly = !hasData && hasUserPrintMessages(messages);

  // Output-only state (PRINT/RAISERROR with no result sets) — render the OutputPanel
  // with a minimal toolbar so the pin button still works.
  if (hasOutputOnly && !isLoading) {
    return (
      <div className={`w-full h-full flex flex-col overflow-hidden bg-background ${className}`} data-testid="results-grid">
        <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-muted/30 border-b border-border select-none">
          <div className="flex-1 text-xs text-muted-foreground px-2">Output</div>
          {onPin && (
            <button
              type="button"
              onClick={onPin}
              className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-background/50"
              title="Pin output"
              data-testid="pin-results-toggle"
            >
              <Pin className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <OutputPanel messages={messages} resultSetCount={0} className="flex-1" />
      </div>
    );
  }

  // Empty state
  if (!hasData && !isLoading) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-background ${className}`}>
        <p className="text-muted-foreground text-sm">No results to display. Run a query to see results here.</p>
      </div>
    );
  }

  // Pre-columns loading state: we render the grid frame (with a toolbar pill
  // and skeleton body) instead of a blocking centered spinner, so the results
  // panel appears immediately on Execute. The toolbar branch below keys on
  // `isAwaitingFirstData` to show the "Executing query…" indicator.
  const isAwaitingFirstData = isLoading && !isPopulating && !hasData;

  // Plain text mode - render PlaintextResultsView instead of grid
  if (displayMode === 'plaintext') {
    const plaintextResultSets = isWindowedMultiResultSet
      ? exportableResultSets.map((rs, i) => {
          const fetched = plaintextRowsByIndex[i];
          if (fetched) return { ...rs, rows: fetched };
          if (i === activeResultSetIndex && wdBufferRows.length > 0) return { ...rs, rows: wdBufferRows };
          return rs;
        })
      : isWindowed && wdBufferRows.length > 0
        ? exportableResultSets.map((rs, i) => (i === 0 ? { ...rs, rows: wdBufferRows } : rs))
        : exportableResultSets;
    const plaintextInfoNote = isWindowed && wdBufferRows.length < effectiveTotalRowCount
      ? isWindowedMultiResultSet
        ? `Showing first ${wdBufferRows.length.toLocaleString()} of ${effectiveTotalRowCount.toLocaleString()} rows for the active result set. Switch result tabs to view others.`
        : `Showing first ${wdBufferRows.length.toLocaleString()} of ${effectiveTotalRowCount.toLocaleString()} rows. Switch to Grid view for full navigation.`
      : undefined;

    return (
      <PlaintextResultsView
        resultSets={plaintextResultSets}
        messages={messages}
        className={className}
        infoNote={plaintextInfoNote}
      />
    );
  }

  // Fix round 1 (C1) — the actions cluster is defined once here so it can
  // render either inline (in this grid's own toolbar row, `showToolbar`
  // true) or portaled into the embedding surface's own output bar
  // (`showToolbar` false + `actionsPortalContainer` supplied). Extracting
  // it avoids duplicating ~25 wired-up props at two call sites.
  const actionsElement = (
    <ResultsGridActions
      totalRows={totalRows}
      isLoading={isLoading || isPopulating}
      showColumnProfiling={showColumnProfiling}
      setShowColumnProfiling={setShowColumnProfiling}
      showResultFilter={showResultFilter}
      setShowResultFilter={setShowResultFilter}
      setColumnFilters={setColumnFilters}
      setGlobalFilter={setGlobalFilter}
      canFilter={canFilter}
      isFilterActive={isFilterActive}
      viewMode={viewMode}
      onPin={onPin}
      onPinToDashboard={
        onPinToDashboard
          ? () =>
              onPinToDashboard(
                activeResultSet
                  ? { columns: activeResultSet.columns, rows: activeResultSet.rows.slice(0, 50) }
                  : undefined,
              )
          : undefined
      }
      canExport={canExport}
      isWindowed={isWindowed}
      isExporting={isExporting}
      copied={copied}
      isCopyMenuOpen={isCopyMenuOpen}
      setIsCopyMenuOpen={setIsCopyMenuOpen}
      isExportMenuOpen={isExportMenuOpen}
      setIsExportMenuOpen={setIsExportMenuOpen}
      handleCopy={handleCopy}
      handleExport={handleExport}
      handleExportAllCsv={handleExportAllCsv}
      bufferRowCount={isWindowed ? wdBufferRows.length : sortedResults.length}
      windowedTotalRowCount={windowedTotalRowCount}
      remoteTotalRowCount={remoteTotalRowCount}
      exportableResultSets={exportableResultSets}
      handleToggleRowSidePanel={handleToggleRowSidePanel}
      isRowSidePanelOpen={isRowSidePanelOpen}
      hasMultipleResultSets={hasMultipleResultSets}
      setViewMode={setViewMode}
      stackViewStatus={stackViewStatus}
    />
  );

  return (
    <div className={`w-full h-full flex flex-col overflow-hidden bg-background ${className}`} data-testid="results-grid">
      {/* Results toolbar (tabs/stack toggle + export). PP-16 + fix round 1
          (C1): an embedding surface that already owns a single output bar
          (e.g. a notebook cell with one result set) suppresses this whole
          bar via `showToolbar={false}` rather than stacking a second bar
          under its own — see the prop doc for why multi-result-set callers
          keep it on. The actions cluster itself is never dropped: when the
          bar is suppressed it portals into `actionsPortalContainer`
          instead (below), so filter/profiling/pin/export/copy stay
          reachable. */}
      {showToolbar && (
      <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-muted/30 border-b border-border select-none" data-testid="results-grid-toolbar">
        {hasMultipleResultSets ? (
          <>
            {/* Result set tabs (only visible in tabs mode) */}
            {viewMode === 'tabs' && (
              <div className="flex items-center gap-1 overflow-x-auto flex-1">
                {showOutputTab && (
                  <button
                    onClick={() => setIsOutputActive(true)}
                    className={`
                      px-3 py-1 text-xs rounded-md whitespace-nowrap transition-colors
                      ${isOutputActive
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                      }
                    `}
                    data-testid="output-result-tab"
                  >
                    Output
                  </button>
                )}
                {resultSets!.map((rs, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setIsOutputActive(false);
                      setActiveResultSetIndex(index);
                    }}
                    className={`
                      px-3 py-1 text-xs rounded-md whitespace-nowrap transition-colors
                      ${!isOutputActive && index === activeResultSetIndex
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                      }
                    `}
                    data-testid={`result-set-tab-${index}`}
                  >
                    Result {index + 1}
                    {rs.rowCount !== undefined && (
                      <span className="ml-1 text-muted-foreground">
                        ({formatRowCount(rs.rowCount)})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Stack mode indicator */}
            {viewMode === 'stack' && (
              <div className="flex-1 text-xs text-muted-foreground px-2">
                Showing all {resultSets!.length} result sets stacked
              </div>
            )}

            {/* Population status — parity with the single-result-set branch
                below. A multi-statement batch (>50K rows per set, windowed)
                keeps populating the ACTIVE set after its first window paints,
                and while it does the header click is a no-op (`canSort` is
                false). Without this line the tabs layout gave the user no
                signal at all — only the header tooltip — and the e2e helper
                that gates sorting on this indicator was blind here, which is
                how the PG per-result-set sort spec went red (2026-09-11). */}
            {isPopulating && (
              <div className="text-xs px-2 select-text">
                <GridStatusInline
                  phase={{
                    kind: 'populating',
                    loadedRowCount: effectiveTotalRowCount,
                  }}
                  onCancel={onCancelPopulation}
                  isCancelling={isCancelling}
                  isLongRunning={isLongRunning}
                  testIdPopulating="results-populating-indicator"
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 text-xs px-2 select-text">
            {isPopulating ? (
              <GridStatusInline
                phase={{
                  kind: 'populating',
                  loadedRowCount: effectiveTotalRowCount,
                }}
                onCancel={onCancelPopulation}
                isCancelling={isCancelling}
                isLongRunning={isLongRunning}
                testIdPopulating="results-populating-indicator"
              />
            ) : isAwaitingFirstData ? (
              <GridStatusInline
                phase={{ kind: 'awaiting' }}
                onCancel={onCancelPopulation}
                isCancelling={isCancelling}
                isLongRunning={isLongRunning}
                testIdAwaiting="results-executing-indicator"
              />
            ) : showRowCountSummary ? (
              <span className="text-muted-foreground tabular-nums">
                {(() => {
                  if (isWindowed) {
                    const total = effectiveTotalRowCount;
                    if (isFilterActive && windowedTotalRowCount !== remoteTotalRowCount) {
                      return `${windowedTotalRowCount.toLocaleString()} of ${formatRowCount(remoteTotalRowCount ?? 0)}`;
                    }
                    return formatRowCount(total);
                  }
                  const rs = exportableResultSets[0];
                  if (!rs) return 'Results';
                  const total = rs.rowCount ?? (Array.isArray(rs.rows) ? rs.rows.length : 0);
                  if (isFilterActive) {
                    return `${filteredResults.length.toLocaleString()} of ${formatRowCount(total)}`;
                  }
                  return formatRowCount(total);
                })()}
              </span>
            ) : null}
          </div>
        )}

        {/* Edit mode controls (before divider for discoverability) */}
        {editMode && (
          <ResultsEditToolbar
            hasEditableColumns={editMode.hasEditableColumns}
            readOnlyReason={editMode.readOnlyReason}
            isEditMode={editMode.isEditMode}
            pendingChangeCount={editMode.pendingChangeCount}
            isSaving={editMode.isSaving}
            onToggleEditMode={editMode.onToggleEditMode}
            onSave={editMode.onSave}
            onDiscard={editMode.onDiscard}
            onPreview={editMode.onPreview}
          />
        )}

        {actionsElement}
      </div>
      )}

      {/* Fix round 1 (C1) — `showToolbar={false}` surfaces (single-result-set
          notebook cells) get their actions cluster portaled into their own
          output bar instead of losing it. No-op (renders nothing) when the
          embedding surface hasn't supplied a container yet. */}
      {!showToolbar && actionsPortalContainer && createPortal(actionsElement, actionsPortalContainer)}

      {/* Stacked view (when stack mode is active and we have multiple result sets) */}
      {viewMode === 'stack' && hasMultipleResultSets && resultSets && (
        <StackedResultsView
          resultSets={
            isWindowedMultiResultSet
              ? resultSets.map((rs, i) => {
                  const fetched = plaintextRowsByIndex[i];
                  return fetched ? { ...rs, rows: fetched } : rs;
                })
              : resultSets
          }
          topSlot={showOutputTab ? (
            <StackedOutputSection
              messages={messages}
              resultSetCount={resultSets.length}
              onSelectResultSet={handleSelectResultSetFromOutput}
            />
          ) : undefined}
        />
      )}

      {/* Output tab content (when active in tabs mode) */}
      {viewMode === 'tabs' && hasMultipleResultSets && showOutputTab && isOutputActive && (
        <OutputPanel
          messages={messages}
          resultSetCount={resultSets!.length}
          onSelectResultSet={handleSelectResultSetFromOutput}
        />
      )}

      {/* Scrollable container for both horizontal and vertical scrolling (tabs mode) */}
      {!isOutputActive && (viewMode === 'tabs' || !hasMultipleResultSets) && (
        <div ref={resultsSplitRef} className="flex-1 min-h-0 overflow-hidden flex relative">
          {/* Spinner overlay for remote sort/filter operations.
              pointer-events-none lets header clicks pass through during sort —
              the sort token in useWindowedGrid already serializes rapid clicks. */}
          {isWindowed && !isPopulating && isRemoteSorting && (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-background/60">
              <div className="flex items-center gap-2">
                <RandomDotmSpinner size={16} className="text-primary" />
                <span className="text-muted-foreground text-sm">Sorting...</span>
              </div>
            </div>
          )}
          {/* Population progress is shown in the toolbar row count area above */}
          {/* Failed source error banner — shown when the windowed source has failed */}
          {wdSourceError && (
            <div
              className="flex-1 min-w-0 min-h-0 flex items-center justify-center"
              data-testid="source-error-banner"
            >
              <div className="flex flex-col items-center gap-3 max-w-md text-center px-6">
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <p className="text-sm text-muted-foreground">{wdSourceError.message}</p>
                {onReExecute && (
                  <button
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    onClick={onReExecute}
                    data-testid="source-error-rerun-button"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Re-run query
                  </button>
                )}
              </div>
            </div>
          )}
          {!wdSourceError && <div
            ref={containerRef}
            onScroll={onScroll}
            onKeyDown={isProportional ? onGridKeyDown : undefined}
            tabIndex={isProportional ? 0 : undefined}
            // [overflow-anchor:none] - see TableExplorerGrid's scroll container: scroll
            // anchoring must not react to the gap branch's skeleton<->row swaps.
            className="flex-1 min-w-0 min-h-0 overflow-auto focus:outline-none [overflow-anchor:none]"
          >
            {/* PP-05 — `minWidth: 100%` keeps the grid spanning the pane when the
                columns are narrower than it; the trailing filler cell (below and in
                `GridHeaderRow`) absorbs the slack so real columns keep their widths. */}
            <table
              ref={tableRef}
              className={`border-collapse text-sm ${isFixedLayout ? 'table-fixed' : ''}`}
              style={{ minWidth: '100%', ...(isFixedLayout ? { width: table.getTotalSize() } : {}), ...(zoomLevel !== 1 ? { zoom: zoomLevel } : {}), ...pinnedOffsetVars }}
            >
              {/* Explicit column widths live HERE (fixed layout) rather than on
                  every td — a resize drag then repaints via colgroup + the CSS
                  pin variables above with zero row re-renders. In auto layout
                  the cols carry no width and content sizing rules as before. */}
              <colgroup>
                {/* Pinning-AWARE order (left, center, right) — getVisibleLeafColumns()
                    is visibility-only and would misalign the cols against the pinned
                    header/cell order, handing columns their neighbours' widths on
                    the auto->fixed flip. */}
                {[
                  ...table.getLeftVisibleLeafColumns(),
                  ...table.getCenterVisibleLeafColumns(),
                  ...table.getRightVisibleLeafColumns(),
                ].map((column) => (
                  <col
                    key={column.id}
                    style={columnSizing[column.id] !== undefined ? { width: column.getSize() } : undefined}
                  />
                ))}
                {/* PP-05 filler column */}
                <col />
              </colgroup>
              {/* Header row — memoized element; see `headerRowContent` above. */}
              {headerRowContent}
              {/* ONE delegated context menu for every data cell. The per-cell
                  Radix menu was the dominant scroll-render cost on wide results
                  (74 columns x ~50 rows = ~3,700 menu roots re-created per
                  scroll tick). The tbody is the trigger; the capture handler
                  resolves the target cell from `data-row-index`/`data-column-id`
                  before Radix opens. */}
              <CellContextMenu
                value={menuDisplayedValue}
                columnName={menuColumn?.name}
                onOpenChange={handleCellMenuOpenChange}
                onPinToDashboard={
                  onPinToDashboard
                    ? () =>
                        onPinToDashboard(
                          activeResultSet
                            ? {
                                columns: activeResultSet.columns,
                                rows: activeResultSet.rows.slice(0, 50),
                              }
                            : undefined,
                        )
                    : undefined
                }
                canEdit={!!(menuCell && editMode?.hasEditableColumns && editMode.isColumnEditable(menuCell.columnId))}
                canToggleNumericGrouping={!!menuCell && numericColumnIds.has(menuCell.columnId)}
                isEditMode={!!editMode?.isEditMode}
                onEnterEditMode={editMode?.onToggleEditMode}
                onEdit={() => {
                  if (menuCell) setEditingCell({ rowIndex: menuCell.rowIndex, columnId: menuCell.columnId });
                }}
                onCopySelection={cellSelection.selectedCells.size > 1 ? () => {
                  const colKeyMap = new Map(normalizedColumns.map((c) => [c.id, c.key]));
                  const colNames = new Map(normalizedColumns.map((c) => [c.id, c.name]));
                  const colIds = normalizedColumns.map((c) => c.id);
                  const copyRows = isWindowed ? wdBufferRows : sortedResults;
                  const copyOffset = isWindowed ? wdBufferStart : 0;
                  if (getCellValueFn) {
                    setIsCopying(true);
                    void buildCopyTextAsync(cellSelection.selectedCells, copyRows, colIds, colKeyMap, copyOffset, getCellValueFn, sourceGeneration, colNames, spatialCopyTransform)
                      .then(({ text, html, usedPreview, staleCount }) => {
                        void writeRichToClipboard(text, html);
                        if (staleCount > 0) {
                          toast.default({ title: 'Stale values', description: `${staleCount} cell(s) used preview values due to concurrent changes.` });
                        } else if (usedPreview) {
                          toast.default({ title: 'Large selection', description: 'Copied preview values. Use Export for full data.' });
                        } else if (hasOutOfBufferCells(cellSelection.selectedCells, copyOffset, copyRows.length)) {
                          toast.default({ title: 'Partial copy', description: 'Copied from loaded rows. Use Export for full dataset.' });
                        }
                      })
                      .finally(() => setIsCopying(false));
                  } else {
                    const text = buildCopyText(cellSelection.selectedCells, copyRows, colIds, colKeyMap, copyOffset, spatialCopyTransform);
                    const html = buildCopyHtml(cellSelection.selectedCells, copyRows, colIds, colNames, colKeyMap, copyOffset, spatialCopyTransform);
                    void writeRichToClipboard(text, html);
                  }
                } : undefined}
                onCopyCell={() => {
                  if (!menuCell) return;
                  const stringifyForCopy = (raw: unknown) =>
                    raw === null || raw === undefined ? '' : (typeof raw === 'object' ? JSON.stringify(raw) : String(raw));
                  if (getCellValueFn) {
                    setIsCopying(true);
                    void getCellValueFn(menuCell.rowIndex, menuCell.columnId)
                      .then((result) => writeClipboardText(stringifyForCopy(spatialCopyTransform(result.value, menuCell.columnId))))
                      .catch((err) => {
                        toast.default({ title: 'Cell fetch failed', description: err instanceof Error ? err.message : 'Using preview value' });
                        return writeClipboardText(stringifyForCopy(spatialCopyTransform(menuDisplayedValue, menuCell.columnId)));
                      })
                      .finally(() => setIsCopying(false));
                  } else {
                    void writeClipboardText(stringifyForCopy(spatialCopyTransform(menuDisplayedValue, menuCell.columnId)));
                  }
                }}
                onCopyRow={() => {
                  if (!menuCell) return;
                  const copyRows = isWindowed ? wdBufferRows : sortedResults;
                  const copyOffset = isWindowed ? wdBufferStart : 0;
                  setIsCopying(true);
                  void buildCopyRowText(menuCell.rowIndex, copyRows, normalizedColumns, copyOffset, getCellValueFn, sourceGeneration, spatialCopyTransform)
                    .then((text) => writeClipboardText(text))
                    .finally(() => setIsCopying(false));
                }}
                isCopying={isCopying}
                onAddToWhere={onAddToWhere && menuColumn ? (name, value) => {
                  const target = resultColumnTarget(menuColumn.id);
                  if (target) onAddToWhere(name, value, { ...target, name });
                } : undefined}
                onProfileColumn={(profCol) => {
                  if (!menuCell) return;
                  const meta = columnById.get(menuCell.columnId);
                  if (!meta?.sourceTable || !connectionId) return;
                  openDataProfileTab({
                    connectionId,
                    database: database ?? '',
                    schema: meta.sourceSchema ?? '',
                    table: meta.sourceTable,
                    focusedColumn: meta.sourceColumn ?? profCol,
                    dataMode: 'sample',
                    entryPoint: 'grid_cell',
                  });
                }}
                canDeclareAsJson={!!menuCell && declarableJsonColumnIds.has(menuCell.columnId)}
                onDeclareAsJson={() => menuCell && handleDeclareAsJson(menuCell.columnId, 'cell-menu')}
                canShowJsonDetails={!!menuCell && declaredJsonColumnIds.has(menuCell.columnId)}
                onShowJsonDetails={() => menuCell && handleShowJsonDetails(menuCell.columnId, 'cell-menu')}
                canEditLooseFk={!!menuCell && looseFkColumnIds.has(menuCell.columnId)}
                onEditLooseFk={() => menuCell && handleEditLooseFkFromMenu(menuCell.columnId, 'cell-menu')}
                canDeclareLooseFk={!!menuCell && looseFkEligibleColumnIds.has(menuCell.columnId)}
                onDeclareLooseFk={() => menuCell && handleDeclareLooseFkFromMenu(menuCell.columnId, 'cell-menu')}
                canCompareGeometry={!!spatialCompareCandidate}
                onCompareGeometry={() => setCompareDialogOpen(true)}
              >
              <tbody
                className={cellSelection.isMultiCell ? 'select-none' : ''}
                onContextMenuCapture={handleGridBodyContextMenuCapture}
              >
                {/* Fast-scroll gap: the live viewport has scrolled clear of the
                    (deferred) rendered window — paint skeletons at the live
                    position for instant feedback; the real rows mount once,
                    where the scroll settles. */}
                {gapSkeletonWindow ? (
                  <>
                    {gapSkeletonWindow.topPaddingHeight > 0 && (
                      <tr aria-hidden="true">
                        <td
                          colSpan={(normalizedColumns.length || 1) + 1}
                          className="p-0 border-0"
                          style={{ height: gapSkeletonWindow.topPaddingHeight }}
                        />
                      </tr>
                    )}
                    <SkeletonRows
                      startIndex={gapSkeletonWindow.startIndex}
                      count={gapSkeletonWindow.count}
                      columnCount={normalizedColumns.length}
                    />
                    {gapSkeletonWindow.bottomPaddingHeight > 0 && (
                      <tr aria-hidden="true">
                        <td
                          colSpan={(normalizedColumns.length || 1) + 1}
                          className="p-0 border-0"
                          style={{ height: gapSkeletonWindow.bottomPaddingHeight }}
                        />
                      </tr>
                    )}
                  </>
                ) : (
                  <>
                  {/* Top padding for virtualization */}
                  {topPaddingHeight > 0 && (
                    <tr aria-hidden="true">
                      <td
                        colSpan={(normalizedColumns.length || 1) + 1}
                        className="p-0 border-0"
                        style={{ height: topPaddingHeight }}
                      />
                    </tr>
                  )}

                  {/* Skeleton rows before buffer (windowed mode only) */}
                  {skeletonRanges && skeletonRanges.before.count > 0 && (
                    <SkeletonRows
                      startIndex={skeletonRanges.before.start}
                      count={skeletonRanges.before.count}
                      columnCount={normalizedColumns.length}
                    />
                  )}

                  {/* Pre-data skeleton: headers are known but no rows have arrived
                      yet. Covers fast-path streaming between first "columns"
                      progress event and first row batch, and windowed mode before
                      the initial buffer fetch resolves. */}
                  {isLoading
                    && normalizedColumns.length > 0
                    && table.getRowModel().rows.length === 0
                    && (!skeletonRanges || skeletonRanges.before.count === 0) && (
                    <SkeletonRows
                      startIndex={0}
                      count={20}
                      columnCount={normalizedColumns.length}
                    />
                  )}

                  {/* Visible rows — memoized: on scroll, rows already on screen
                      skip re-rendering; only rows entering the overscan window
                      mount (see ResultsGridDataRow). */}
                  {table.getRowModel().rows.map((row, rowIndex) => {
                    const actualRowIndex = dataStartIndex + rowIndex;
                    return (
                      <ResultsGridDataRow
                        key={row.id}
                        row={row}
                        actualRowIndex={actualRowIndex}
                        isSelected={selectedRowIndex === actualRowIndex}
                        editingColumnId={editingCell?.rowIndex === actualRowIndex ? editingCell.columnId : null}
                        menuColumnId={menuCell?.rowIndex === actualRowIndex ? menuCell.columnId : null}
                        selectionSignature={selectionSignatureByRow?.get(actualRowIndex) ?? ''}
                        ctx={rowCtx}
                      />
                    );
                  })}

                  {/* Skeleton rows after buffer (windowed mode only) */}
                  {skeletonRanges && skeletonRanges.after.count > 0 && (
                    <SkeletonRows
                      startIndex={skeletonRanges.after.start}
                      count={skeletonRanges.after.count}
                      columnCount={normalizedColumns.length}
                    />
                  )}

                  {/* Bottom padding for virtualization */}
                  {bottomPaddingHeight > 0 && (
                    <tr aria-hidden="true">
                      <td
                        colSpan={(normalizedColumns.length || 1) + 1}
                        className="p-0 border-0"
                        style={{ height: bottomPaddingHeight }}
                      />
                    </tr>
                  )}
                  </>
                )}
              </tbody>
              </CellContextMenu>
            </table>
          </div>}

          {isRowSidePanelOpen && viewMode !== 'stack' && (
            <>
              <div
                className="flex-shrink-0 w-2 cursor-col-resize flex items-center justify-center relative bg-transparent before:absolute before:inset-y-0 before:left-1/2 before:-translate-x-1/2 before:w-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                onPointerDown={rowSidePanelSplitter.onPointerDown}
                onDoubleClick={rowSidePanelSplitter.onDoubleClick}
                onKeyDown={rowSidePanelSplitter.onKeyDown}
                style={{ touchAction: 'none', userSelect: 'none' }}
                data-testid="results-row-side-panel-resize-divider"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize row side panel"
                title="Drag to resize"
                aria-valuenow={rowSidePanelWidthPx}
                aria-valuemin={ROW_SIDE_PANEL_MIN_WIDTH}
                aria-valuemax={ROW_SIDE_PANEL_MAX_WIDTH}
                aria-valuetext={`${rowSidePanelWidthPx} pixels`}
                tabIndex={0}
                data-dragging={rowSidePanelSplitter.isDragging ? 'true' : undefined}
              >
                <div className="h-8 w-1 bg-border rounded-full" />
              </div>
              <RowSidePanelContent
                selectedRowIndex={selectedRowIndex}
                selectedRow={selectedRow}
                normalizedColumns={normalizedColumns.map((col) => ({
                  ...col,
                  isJson: jsonColumnIdSet.has(col.id),
                  isJsonDeclared: declaredJsonColumnIds.has(col.id),
                  isEnum: enumDeclaredLikeColumnIds.has(col.id) || nativeEnumColumnIds.has(col.id),
                  isEnumDeclared: enumDeclaredLikeColumnIds.has(col.id),
                }))}
                editMode={versionBumpingEditMode}
                sidePanelEditingColumn={sidePanelEditingColumn}
                setSidePanelEditingColumn={setSidePanelEditingColumn}
                onClose={() => setIsRowSidePanelOpen(false)}
                style={{ width: rowSidePanelWidthPx }}
                openExpandedTextDialog={openExpandedTextDialog}
                copyValueTransform={spatialCopyTransform}
                buildSmartContext={(columnId, row) => {
                  const col = normalizedColumns.find((c) => c.id === columnId);
                  if (!validEngine || !connectionId || !database || !col?.sourceTable) return undefined;
                  const realFkKey = resultSetFkKey(col);
                  const realFkTarget = realFkKey ? realFkMap.get(realFkKey) : undefined;
                  return {
                    connectionId,
                    database,
                    engine: validEngine,
                    schema: col.sourceSchema ?? '',
                    table: col.sourceTable,
                    column: col.sourceColumn ?? col.name,
                    enumDeclared: enumDeclaredLikeColumnIds.has(columnId),
                    enumNative: nativeEnumColumnIds.has(columnId),
                    realFkTarget,
                    looseRelCandidates: col.sourceColumn
                      ? (metaInfo?.looseRelationships ?? []).filter(
                          (rel) =>
                            rel.fromSchema === (col.sourceSchema ?? '') &&
                            rel.fromTable === col.sourceTable &&
                            rel.fromColumn === col.sourceColumn,
                        )
                      : [],
                    sourceRow: row,
                    dataType: col.dataType,
                    nullable: col.nullable,
                  };
                }}
              />
            </>
          )}
        </div>
      )}

      <CellAggregateBar aggregates={cellAggregates} />

      {/* Expanded Value Dialog for JSON and large text content */}
      {(() => {
        const canEditExpanded = !!(
          expandedTextDialog?.columnId && expandedTextDialog?.rowData &&
          editMode?.isEditMode && editMode.isColumnEditable(expandedTextDialog.columnId)
        );
        const expandedColumn = expandedTextDialog?.columnId
          ? normalizedColumns.find((c) => c.id === expandedTextDialog.columnId)
          : undefined;
        const expandDialogDeclarableJsonContext = (() => {
          if (!expandedTextDialog?.columnId) return undefined;
          if (!validEngine || !connectionId || !database) return undefined;
          if (!expandedColumn?.sourceTable || !expandedColumn.sourceColumn || !expandedColumn.dataType) return undefined;
          if (forcedJsonColumnIds.has(expandedTextDialog.columnId)) return undefined;
          if (!declarableJsonColumnIds.has(expandedTextDialog.columnId)) return undefined;
          return {
            connectionId,
            database,
            schema: expandedColumn.sourceSchema ?? '',
            table: expandedColumn.sourceTable,
            column: expandedColumn.sourceColumn,
            dataType: expandedColumn.dataType,
            engine: validEngine,
          };
        })();
        return (
          <ExpandedValueDialog
            open={expandedTextDialog !== null}
            onOpenChange={(open) => {
              if (!open) setExpandedTextDialog(null);
            }}
            title={expandedTextDialog?.isLoadingFullValue ? `${expandedTextDialog?.title ?? 'Text'} (loading full value…)` : (expandedTextDialog?.title ?? 'Text')}
            initialValue={expandedTextDialog?.value ?? ''}
            initialMode={expandedTextDialog?.mode ?? 'text'}
            readOnly={!canEditExpanded}
            onRequestEdit={
              !canEditExpanded && expandedTextDialog?.columnId && expandedTextDialog?.rowData &&
              editMode?.hasEditableColumns && editMode.isColumnEditable(expandedTextDialog.columnId)
                ? () => {
                  editMode?.onToggleEditMode();
                }
                : undefined
            }
            onConfirm={(newValue) => {
              if (expandedTextDialog?.columnId && expandedTextDialog?.rowData && versionBumpingEditMode) {
                versionBumpingEditMode.onCellEdit(
                  expandedTextDialog.rowData,
                  expandedTextDialog.columnId,
                  expandedTextDialog.value,
                  newValue
                );
              }
              setExpandedTextDialog(null);
            }}
            declarableJsonContext={expandDialogDeclarableJsonContext}
            spatial={expandedTextDialog?.spatial}
          />
        );
      })()}

      {/* T30 — two-cell geometry compare */}
      {spatialCompareCandidate && (
        <SpatialCompareDialog
          open={compareDialogOpen}
          onOpenChange={setCompareDialogOpen}
          a={{
            input: spatialCompareCandidate.a.input,
            fetchFullValue: getFullSpatialValue
              ? () => getFullSpatialValue(spatialCompareCandidate.a.rowIndex, spatialCompareCandidate.a.columnId)
              : undefined,
          }}
          b={{
            input: spatialCompareCandidate.b.input,
            fetchFullValue: getFullSpatialValue
              ? () => getFullSpatialValue(spatialCompareCandidate.b.rowIndex, spatialCompareCandidate.b.columnId)
              : undefined,
          }}
          connectionId={connectionId}
          database={database}
        />
      )}

      {/* Export Limit Dialog (Personal Mode) */}
      <PersonalModeExportLimitMessage
        open={showExportLimitDialog}
        onOpenChange={setShowExportLimitDialog}
        rowCount={exportLimitRowCount}
        onUpgrade={() => {
          setShowExportLimitDialog(false);
          setShowUpgradeDialog(true);
        }}
      />

      {/* Upgrade Dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />
    </div>
  );
});
